import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import Table from '../../shared/components/layout/Table';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import filter from '../../../modules/shared/assets/svg/filter.svg';
import Header from '../../shared/components/layout/Header';
import Layout from '../components/layout/AppLayout';
import Search from '../../shared/components/ui/Search';
import CalenderIcon from '../../shared/assets/svg/calendar.svg';
import LeadsData from '../static/data/leads.json';
import { transformData } from '../../super-admin/utils/transformData';
import { getNextFollowUpForLead } from '../utils/getNextFollowUpForLead';
import { getLeadRemark } from '../utils/getLeadRemark';
import {
  LEAD_SOURCE_FILTER_OPTIONS,
  getLeadSourceFilterLabel
} from '../constants/leadSourceOptions';
import { getLeadSourceDisplay } from '../utils/getLeadSourceDisplay';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  navigateWithReturn
} from '../../shared/navigation';
import {
  buildSalesListFilterParams,
  getSalesListFiltersFromSearch
} from './Sales/salesUrlParams';
import {
  createLead,
  getLeadsByStatusAndProject,
  getLeadCardsStatsPerUser,
  deleteLead,
  handleCheckLeadDelete,
  updateLead,
  cloneLead,
  downloadLeadTemplate,
  uploadLeadTemplate,
  getAllSalesUsers,
  getAllManagementUsers,
  getAllSalesUsersByProjectId,
  getMyData,
  createDuplicateLeads,
  getLeadQualificationQuestions
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import NoDataFound from '../components/layout/NoDataFound';
import Modal from '../../shared/components/ui/Modal';
import AddSalesTarget from '../components/forms/AddSalesTarget';
import UpdateSalesUser from '../components/forms/UpdateSalesUser';
import BulkTransferSalesUser from '../components/forms/BulkTransferSalesUser';
import QualificationQuestionsBankModal from '../components/lead/QualificationQuestionsBankModal';
import AddLeadForm from '../components/forms/AddLeadForm';
import EditLeadForm from '../components/forms/EditLeadForm';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import noDataIcon from '../../shared/assets/svg/NoData.svg';
import DP from '../../shared/assets/svg/noUser.svg';
import NoDataFoundImg from '../../shared/assets/png/noDataFound.png';
import {
  ChevronDown,
  ChevronLeft,
  Download,
  Upload,
  Users,
  Flame,
  Snowflake,
  Sun,
  PauseCircle,
  CalendarClock,
  MonitorPlay,
  Plus,
  MessageCircle,
  Trash2
} from 'lucide-react';
import FileDownload from '../../shared/assets/svg/download.svg';
// import editIcon from '../../shared/assets/svg/edit.svg'; // Edit action moved to Sales Details
// import deleteIcon from '../../shared/assets/svg/delete.svg'; // Delete moved to Sales Details
import Multiselect from 'multiselect-react-dropdown';
import DeleteConfirmationModal from './../components/ui/DeleteConfirmationModal';
import Loader from '../../shared/components/ui/Loader';
import Headroom from 'react-headroom';
import mixpanel from 'mixpanel-browser';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import UploadResultsModal from '../components/ui/UploadResultsModal';
import { normalizeTemplateUploadResponse } from '../utils/templateUploadResponse';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import MetricCard from '../components/ui/MetricCard';
import LeadsAiSummaryBanner, {
  LeadAiScoreBadge,
  LeadsAiBrief,
  useClientAiFilterLeadIds,
  useLeadAiScores
} from '../components/ai/LeadsAiSummaryBanner';
import { LeadProgressionFlowCard } from '../components/sales/LeadProgressionFlowCard';
import { isClientAiFilter } from '../components/ai/aiListDeepLinks';
import BulkSendFlowModal from '../components/whatsapp/BulkSendFlowModal';
import { useInboxStream } from '../hooks/useInboxStream';
import {
  applyConversationUpdateToLeads,
  normalizeLeadPhone
} from '../utils/leadWhatsAppUtils';
import { SITE_VISIT_SCHEDULED_LABEL, stageDisplayName } from '../utils/requiresNextFollowUpFields';

const normalizeStatusFilter = (statusValue) => {
  if (Array.isArray(statusValue)) {
    return statusValue.filter((status) => status && status !== 'all');
  }

  if (typeof statusValue === 'string') {
    const trimmedValue = statusValue.trim();

    if (!trimmedValue || trimmedValue === 'all') {
      return [];
    }

    return trimmedValue
      .split(',')
      .map((status) => status.trim())
      .filter((status) => status && status !== 'all');
  }

  return [];
};

const getStatusParam = (selectedStatuses = []) => selectedStatuses.join(',');

/** Main leads list — every pipeline status except Closed Lost (dashboard-only). */
const MAIN_LEADS_PSTATUS = [
  'Hot',
  'Warm',
  'Cold',
  'Demo',
  'Nurture',
  'Future Prospect',
  'Confirmed',
  'Closed Won'
];

// Maximum leads selectable for a WhatsApp flow bulk send (sent in chunks of 50).
const WA_MAX_LEADS = 500;

/** metrics = summary cards only; list = search/toolbar + table; full = entire page */
export const SalesList = ({
  statusFilter = null,
  variant = 'full',
  forcedProjectID = null,
  aiFilter = null,
  /** When false, parent owns the inbox SSE (e.g. SalesMain) — avoid a second connection. */
  ownInboxStream = true,
  onRegisterConversationHandler = null
}) => {
  const [LeadId, setLeadId] = useState();
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [searchQuery, setSearchQuery] = useState('');
  const [secondaryTab, setSecondaryTab] = useState('Overview');
  const [openAddTargetModal, setOpenAddTargetModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddLeadActionModalOpen, setIsAddLeadActionModalOpen] = useState(false);
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [isEditLeadModalOpen, setIsEditLeadModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [isCloneLeadModalOpen, setIsCloneLeadModalOpen] = useState(false);
  const [cloningLead, setCloningLead] = useState(null);
  const [selectedCloneProjects, setSelectedCloneProjects] = useState([]);
  const [sales, setSales] = useState(null);
  const { collapsed } = useSidebar();
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulk, setIsBulk] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  // Bulk WhatsApp flow send (independent from the delete-oriented bulk state above)
  const [waBulkMode, setWaBulkMode] = useState(false);
  const [waSelectedLeadIds, setWaSelectedLeadIds] = useState([]);
  const [waFlowPickerOpen, setWaFlowPickerOpen] = useState(false);
  // Bulk lead transfer (managers / management) — independent of WhatsApp + delete bulk
  const [transferBulkMode, setTransferBulkMode] = useState(false);
  const [transferSelectedIds, setTransferSelectedIds] = useState([]);
  const [isBulkTransferModalOpen, setIsBulkTransferModalOpen] = useState(false);
  // Bulk lead delete (managers / management) — independent of WhatsApp + transfer bulk
  const [deleteBulkMode, setDeleteBulkMode] = useState(false);
  const [deleteSelectedIds, setDeleteSelectedIds] = useState([]);
  const [pstatus, setpStatus] = useState(() => {
    const fromProp = normalizeStatusFilter(statusFilter);
    if (fromProp.length > 0) return fromProp;
    try {
      const fromUrl = getSalesListFiltersFromSearch(
        new URLSearchParams(window.location.search)
      );
      return normalizeStatusFilter(fromUrl.status);
    } catch {
      return [];
    }
  });
  const [closureDateFilter, setClosureDateFilter] = useState(null);
  const [lastFollowUpFilter, setLastFollowUpFilter] = useState(null);
  const [nextFollowUpFilter, setNextFollowUpFilter] = useState(null);
  const [nextFollowUpFromFilter, setNextFollowUpFromFilter] = useState(null);
  const [nextFollowUpToFilter, setNextFollowUpToFilter] = useState(null);
  const [createdOnFilter, setCreatedOnFilter] = useState(null);
  const [createdFromFilter, setCreatedFromFilter] = useState(null);
  const [createdToFilter, setCreatedToFilter] = useState(null);
  const [datesPopover, setDatesPopover] = useState(null); // { dates, anchorRect }
  const [sourceFilter, setSourceFilter] = useState(null);
  const [qualificationTagsFilter, setQualificationTagsFilter] = useState([]);
  const [showQualQuestionsModal, setShowQualQuestionsModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState(() => {
    const defaultFilters = {
      project: null,
      sales: null,
      salesId: null,
      status: [],
      closureDate: null,
      lastFollowUp: null,
      nextFollowUp: null,
      nextFollowUpFrom: null,
      nextFollowUpTo: null,
      createdOn: null,
      createdFrom: null,
      createdTo: null,
      source: null,
      qualificationTags: []
    };

    // Prefer URL filters, then localStorage
    try {
      const fromUrl = getSalesListFiltersFromSearch(
        new URLSearchParams(window.location.search)
      );
      const hasUrlFilters = Object.entries(fromUrl).some(([key, value]) => {
        if (key === 'status' || key === 'qualificationTags') {
          return Array.isArray(value) && value.length > 0;
        }
        return value != null && value !== '';
      });
      if (hasUrlFilters) {
        return {
          ...defaultFilters,
          ...fromUrl,
          // Only honor explicit list `project` query — never dashboard projectID via localStorage
          project: fromUrl.project || null,
          status: normalizeStatusFilter(fromUrl.status)
        };
      }
    } catch {
      // fall through
    }

    const savedFilters = localStorage.getItem('salesListFilters');
    if (!savedFilters) {
      return defaultFilters;
    }

    try {
      const parsedFilters = JSON.parse(savedFilters);
      return {
        ...defaultFilters,
        ...parsedFilters,
        // Leads list always opens on All Projects unless URL/forced says otherwise
        project: null,
        status: normalizeStatusFilter(parsedFilters?.status)
      };
    } catch (error) {
      console.error('Failed to parse sales list filters from localStorage', error);
      return defaultFilters;
    }
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const multiselectRef = useRef(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isDownloadTemplateModalOpen, setIsDownloadTemplateModalOpen] = useState(false);
  const [isUploadTemplateModalOpen, setIsUploadTemplateModalOpen] = useState(false);
  const [isUploadErrorModalOpen, setIsUploadErrorModalOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccesses, setUploadSuccesses] = useState([]);
  const [uploadDuplicates, setUploadDuplicates] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  const [isProcessingDuplicates, setIsProcessingDuplicates] = useState(false);
  const [downloadProjectId, setDownloadProjectId] = useState('');
  const [downloadSalesId, setDownloadSalesId] = useState('');
  const [uploadProjectId, setUploadProjectId] = useState('');
  const [uploadSalesId, setUploadSalesId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [dotCount, setDotCount] = useState(0);

  const [projectID, setProject] = useState('');
  const scoreByLeadId = useLeadAiScores(forcedProjectID ?? projectID);
  const { matchingIds: clientFilterLeadIds, isLoading: clientFilterLoading } =
    useClientAiFilterLeadIds(forcedProjectID ?? projectID, aiFilter);
  /* 
      Hooks
    */
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  /** Hot/warm/cold total-leads URLs â€” status is fixed by route; hide status filter UI */
  const isLeadTemperatureLockedRoute = useMemo(() => {
    const p = location.pathname.replace(/\/$/, '');
    return (
      p.endsWith('/total-leads/hot') ||
      p.endsWith('/total-leads/warm') ||
      p.endsWith('/total-leads/cold') ||
      p.endsWith('/total-leads/nurture') ||
      p.endsWith('/total-leads/future-prospect')
    );
  }, [location.pathname]);

  const isClosedLostOnlyView = useMemo(() => {
    const normalized = normalizeStatusFilter(statusFilter);
    return normalized.length === 1 && normalized[0] === 'Closed Lost';
  }, [statusFilter]);

  const effectivePstatus = useMemo(() => {
    if (isClosedLostOnlyView) {
      return ['Closed Lost'];
    }
    const filtered = pstatus.filter((status) => status !== 'Closed Lost');
    if (filtered.length > 0) {
      return filtered;
    }
    return MAIN_LEADS_PSTATUS;
  }, [pstatus, isClosedLostOnlyView]);

  const showToolbar = variant === 'full' || variant === 'list' || variant === 'metrics';
  const showMetrics = variant === 'full' || variant === 'metrics';
  const showTable = variant === 'full' || variant === 'list' || variant === 'metrics';

  const userId = Cookies.get('userId');
  const userRole = Cookies.get('userRole');
  const userVertical = Cookies.get('userVertical');
  const userDesignationLevel = Cookies.get('userDesignationLevel');
  // All roles can open assign (including Sales Low); dropdown is scoped by level in UpdateSalesUser
  const canAssignLead = true;
  const canBulkTransferLeads =
    userVertical === 'Management' ||
    (userVertical === 'Sales' &&
      (userDesignationLevel === 'Medium' || userDesignationLevel === 'High'));
  const canDeleteLeads = canBulkTransferLeads;
  const canManageQualQuestions =
    userVertical === 'Management' ||
    userRole?.toLowerCase() === 'builder' ||
    userRole?.toLowerCase() === 'admin' ||
    (userVertical === 'Sales' && userDesignationLevel !== 'Low');

  const { data: qualQuestionsBank } = useQuery({
    queryKey: ['leadQualificationQuestions'],
    queryFn: getLeadQualificationQuestions,
    staleTime: 60_000
  });

  const qualificationTagOptions = useMemo(() => {
    const opts = [];
    const seen = new Set();
    const add = (value, label) => {
      if (!value || seen.has(value)) return;
      seen.add(value);
      opts.push({ value, label: label || value });
    };
    for (const q of qualQuestionsBank?.custom || []) {
      if (q.is_active === false) continue;
      for (const o of q.options || []) add(o.value, o.label);
    }
    return opts;
  }, [qualQuestionsBank]);

  const tagLabelByValue = useMemo(() => {
    const map = {};
    for (const o of qualificationTagOptions) map[o.value] = o.label;
    return map;
  }, [qualificationTagOptions]);

  // Track component mount

  // Track page view
  useEffect(() => {
    mixpanel.track('Sales List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    mixpanel.track('Sales List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Sync pstatus from route/dashboard/AI deep-link — original filter UI unchanged.
  useEffect(() => {
    if (variant !== 'list' && variant !== 'metrics') {
      if (statusFilter) {
        const normalizedStatusFilter = normalizeStatusFilter(statusFilter);
        setpStatus(normalizedStatusFilter);
        mixpanel.track('Status Filter Applied from Dashboard', {
          user_id: userId,
          status: getStatusParam(normalizedStatusFilter) || 'all',
          timestamp: new Date().toISOString()
        });
      }
      return;
    }

    if (isClientAiFilter(aiFilter)) {
      setpStatus([]);
      setActiveFilters((prev) => ({ ...prev, status: [] }));
      return;
    }

    // Explicit parent/route status (e.g. total-leads/hot) wins.
    const fromProp = normalizeStatusFilter(statusFilter ?? []);
    if (fromProp.length > 0) {
      setpStatus(fromProp);
      setActiveFilters((prev) => ({
        ...prev,
        status: fromProp
      }));
      return;
    }

    // Do not wipe Warm/Hot/etc. restored from URL or localStorage when parent
    // passes no statusFilter (normal Leads tab).
  }, [statusFilter, aiFilter, userId, variant]);

  useEffect(() => {
    if (!aiFilter) return;
    setPage(1);
    setAllLeads([]);
    setHasMore(true);
  }, [aiFilter]);

  useEffect(() => {
    if (!isLeadTemperatureLockedRoute) return;
    setOpenDropdown((prev) => (prev === 'status' ? null : prev));
  }, [isLeadTemperatureLockedRoute]);

  const prevForcedProjectIdRef = useRef(forcedProjectID);
  useEffect(() => {
    if (forcedProjectID) {
      setPage(1);
      setProject(forcedProjectID);
      prevForcedProjectIdRef.current = forcedProjectID;
      return;
    }
    // Only reset when leaving a forced project (e.g. total-leads), not on every mount
    if (prevForcedProjectIdRef.current) {
      setProject('');
      setActiveFilters((prev) => (prev.project == null ? prev : { ...prev, project: null }));
      prevForcedProjectIdRef.current = null;
    }
  }, [forcedProjectID]);

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(50); // items per page
  const [allLeads, setAllLeads] = useState([]); // Store all loaded leads
  const [hasMore, setHasMore] = useState(true); // Track if more data is available

  const handleConversationUpdated = useCallback(
    (data) => {
      // Keep created_at order; WA updates should not bump leads to the top.
      setAllLeads((prev) =>
        applyConversationUpdateToLeads(prev, data, { reorder: false })
      );
    },
    []
  );

  useEffect(() => {
    if (!onRegisterConversationHandler) return undefined;
    onRegisterConversationHandler(handleConversationUpdated);
    return () => onRegisterConversationHandler(null);
  }, [onRegisterConversationHandler, handleConversationUpdated]);

  useInboxStream({
    enabled: ownInboxStream,
    onConversationUpdated: handleConversationUpdated
  });

  // const {
  //   data: leads,
  //   refetch: refetchLeads,
  //   isFetching
  // } = useQuery({
  //   queryKey: ['lead', page],
  //   queryFn: () => getAllLead(page, limit),
  //   keepPreviousData: true
  // });

  const status = 'Active';

  const {
    data: leads,
    refetch: refetchLeads,
    isFetching,
    dataUpdatedAt
  } = useQuery({
    queryKey: [
      'lead',
      page,
      status,
      projectID,
      effectivePstatus,
      searchQuery,
      sales,
      closureDateFilter,
      lastFollowUpFilter,
      nextFollowUpFilter,
      nextFollowUpFromFilter,
      nextFollowUpToFilter,
      createdOnFilter,
      createdFromFilter,
      createdToFilter,
      sourceFilter,
      qualificationTagsFilter
    ],
    queryFn: () => {
      console.log('API call triggered:', {
        page,
        projectID,
        effectivePstatus,
        searchQuery,
        sales,
        closureDateFilter,
        lastFollowUpFilter,
        nextFollowUpFilter,
        nextFollowUpFromFilter,
        nextFollowUpToFilter,
        createdOnFilter,
        createdFromFilter,
        createdToFilter,
        sourceFilter,
        qualificationTagsFilter
      });
      return getLeadsByStatusAndProject(
        projectID,
        status,
        effectivePstatus,
        page,
        limit,
        searchQuery,
        sales,
        closureDateFilter,
        lastFollowUpFilter,
        nextFollowUpFilter,
        createdOnFilter,
        sourceFilter,
        qualificationTagsFilter,
        createdFromFilter,
        createdToFilter,
        nextFollowUpFromFilter,
        nextFollowUpToFilter
      );
    },
    onSuccess: (data) => {
      mixpanel.track('Leads API Success', {
        user_id: userId,
        project_id: projectID,
        page: page,
        total_leads: data?.meta?.total || 0,
        search_query: searchQuery,
        sales_filter: sales,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Leads API Failed', {
        user_id: userId,
        project_id: projectID,
        page: page,
        search_query: searchQuery,
        sales_filter: sales,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
    // placeholderData removed
  });

  const { data: leadStatsCards } = useQuery({
    queryKey: ['leadStatsCards', projectID, sales],
    queryFn: () => getLeadCardsStatsPerUser(projectID || undefined, sales || undefined),
    onSuccess: (data) => {
      mixpanel.track('Lead Cards Stats API Success', {
        user_id: userId,
        project_id: projectID || null,
        total_leads: data?.data?.totalLead || 0,
        hot_leads: data?.data?.hotLead || 0,
        warm_leads: data?.data?.warmLead || 0,
        cold_leads: data?.data?.coldLead || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Lead Cards Stats API Failed', {
        user_id: userId,
        project_id: projectID || null,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Reset list when filters change. Use getStatusParam(effectivePstatus) so a new [] reference
  // with the same statuses does not retrigger and clear allLeads without reprocessing.
  useEffect(() => {
    setPage(1);
    setAllLeads([]);
    setHasMore(true);
    setIsLoadingMore(false);
    refetchLeads?.();
  }, [
    projectID,
    getStatusParam(effectivePstatus),
    searchQuery,
    sales,
    closureDateFilter,
    lastFollowUpFilter,
    nextFollowUpFilter,
    nextFollowUpFromFilter,
    nextFollowUpToFilter,
    createdOnFilter,
    createdFromFilter,
    createdToFilter,
    sourceFilter,
    qualificationTagsFilter
  ]);

  // Handle data processing and hasMore calculation when leads data changes
  useEffect(() => {
    if (leads?.data) {
      console.log('Ã°Å¸â€œÅ  Processing leads data:', {
        page,
        dataLength: leads.data.length,
        totalLeads: leads?.meta?.total || 0,
        limit,
        allLeadsLength: allLeads.length
      });

      // Process data based on page
      if (page === 1) {
        setAllLeads(leads.data);
      } else {
        setAllLeads((prev) => {
          // Filter out duplicates by checking existing IDs
          const existingIds = new Set(prev.map((lead) => lead.id));
          const newData = leads.data.filter((lead) => !existingIds.has(lead.id));
          return [...prev, ...newData];
        });
      }

      // Calculate hasMore
      const totalLeads = leads?.meta?.total || 0;
      const currentTotalLeads =
        page === 1 ? leads.data.length : (page - 1) * limit + leads.data.length;
      const isLastPage = leads.data.length < limit;
      const hasMoreData = !isLastPage && currentTotalLeads < totalLeads;

      console.log('Ã°Å¸â€œÅ  hasMore calculation:', {
        page,
        dataLength: leads.data.length,
        totalLeads,
        currentTotalLeads,
        isLastPage,
        hasMoreData,
        limit
      });

      setHasMore(hasMoreData);
      setIsLoadingMore(false);
    }
    // dataUpdatedAt: after a refetch, TanStack Query may reuse the same leads.data
    // reference (structural sharing), so this effect would not run again and allLeads
    // would stay cleared if the filter-reset effect ran twice on remount.
  }, [leads?.data, page, limit, dataUpdatedAt]);

  // Query for sales users by project (for download and upload modals)
  const { data: projectSalesUsers, refetch: fetchProjectSalesUsers } = useQuery({
    queryKey: ['ProjectSalesUsers', downloadProjectId || uploadProjectId],
    queryFn: () => getAllSalesUsersByProjectId(downloadProjectId || uploadProjectId),
    enabled: Boolean(downloadProjectId || uploadProjectId), // Only fetch when a project is selected
    onSuccess: (data) => {
      mixpanel.track('All Sales Users API Success', {
        user_id: userId,
        sales_users_count: data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('All Sales Users API Failed', {
        user_id: userId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const formattedSalesUsers = useMemo(() => {
    return projectSalesUsers?.data?.map((user) => ({
      label: user?.name + (user?.is_active ? '' : ' (Inactive)') || '',
      id: user?.id,
      value: user?.id
    }));
  }, [projectSalesUsers]);

  const isSalesExecutive = userVertical === 'Sales' && userDesignationLevel === 'Low';
  const isSalesVerticalUser = userVertical === 'Sales' && !isSalesExecutive;
  const needsLoggedInUserForFilter =
    isSalesExecutive || (userRole === 'builder' && !isSalesVerticalUser);

  const { data: allSalesVerticalUsers } = useQuery({
    queryKey: ['allSalesVerticalUsers'],
    queryFn: () => getAllSalesUsers(1, 1000),
    enabled: !isSalesExecutive
  });

  const { data: allManagementUsers } = useQuery({
    queryKey: ['allManagementUsers'],
    queryFn: () => getAllManagementUsers(1, 1000),
    enabled: !isSalesVerticalUser && !isSalesExecutive
  });

  const { data: loggedInUserForFilter } = useQuery({
    queryKey: ['assignedFilterLoggedInUser', userId],
    queryFn: getMyData,
    enabled: needsLoggedInUserForFilter
  });

  const salesVerticalUsers = useMemo(() => {
    if (isSalesExecutive) {
      if (!loggedInUserForFilter?.id) return [];
      return [
        {
          id: loggedInUserForFilter.id,
          name: loggedInUserForFilter.name || ''
        }
      ];
    }

    const usersById = new Map();
    const assignableUsers = isSalesVerticalUser
      ? allSalesVerticalUsers?.data || []
      : [...(allSalesVerticalUsers?.data || []), ...(allManagementUsers?.data || [])];

    assignableUsers
      .filter((user) => user.is_active)
      .forEach((user) => {
        if (!usersById.has(user.id)) {
          usersById.set(user.id, { id: user.id, name: user.name || '' });
        }
      });

    if (
      !isSalesVerticalUser &&
      userRole === 'builder' &&
      loggedInUserForFilter?.id &&
      !usersById.has(loggedInUserForFilter.id)
    ) {
      usersById.set(loggedInUserForFilter.id, {
        id: loggedInUserForFilter.id,
        name: loggedInUserForFilter.name || ''
      });
    }

    return Array.from(usersById.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [
    allSalesVerticalUsers,
    allManagementUsers,
    loggedInUserForFilter,
    userRole,
    isSalesVerticalUser,
    isSalesExecutive
  ]);

  // Accommodations are now fetched within the form components based on selected project

  React.useEffect(() => {
    if (projects && isInitialLoad) {
      if (forcedProjectID) {
        const forcedProject = projects?.find((p) => String(p.id) === String(forcedProjectID));
        setProject(forcedProjectID);
        setActiveFilters((prev) => ({
          ...prev,
          project: forcedProject?.name || prev.project || null
        }));
      } else {
        // Default to All Projects when entering the Leads list
        setProject('');
        setActiveFilters((prev) => {
          if (prev.project == null) return prev;
          const next = { ...prev, project: null };
          try {
            localStorage.setItem(
              'salesListFilters',
              JSON.stringify({ ...next, project: null })
            );
          } catch {
            // ignore
          }
          return next;
        });
      }

      // Restore sales filter
      if (activeFilters.salesId) {
        setSales(activeFilters.salesId);
      } else if (activeFilters.sales && salesVerticalUsers?.length > 0) {
        // Fallback for old saved filters without salesId
        const savedSales = salesVerticalUsers.find((user) => user.name === activeFilters.sales);
        if (savedSales) {
          setSales(savedSales.id);
        }
      }

      // Restore other filters (avoid new pstatus [] reference when values unchanged —
      // that retriggers the filter-reset effect and can leave allLeads empty if leads.data
      // is referentially stable from the query cache).
      // Closed Lost archive is driven by statusFilter prop — never strip it from localStorage restore.
      if (variant !== 'list' && !isClosedLostOnlyView) {
        setpStatus((prev) => {
          const next = normalizeStatusFilter(activeFilters.status).filter(
            (status) => status !== 'Closed Lost'
          );
          return getStatusParam(prev) === getStatusParam(next) ? prev : next;
        });
      }
      if (activeFilters.closureDate) setClosureDateFilter(activeFilters.closureDate);
      if (activeFilters.lastFollowUp) setLastFollowUpFilter(activeFilters.lastFollowUp);
      if (activeFilters.nextFollowUp) setNextFollowUpFilter(activeFilters.nextFollowUp);
      if (activeFilters.nextFollowUpFrom) {
        setNextFollowUpFromFilter(activeFilters.nextFollowUpFrom);
      }
      if (activeFilters.nextFollowUpTo) {
        setNextFollowUpToFilter(activeFilters.nextFollowUpTo);
      }
      if (activeFilters.createdOn) setCreatedOnFilter(activeFilters.createdOn);
      if (activeFilters.createdFrom) setCreatedFromFilter(activeFilters.createdFrom);
      if (activeFilters.createdTo) setCreatedToFilter(activeFilters.createdTo);
      if (activeFilters.source) setSourceFilter(activeFilters.source);
      if (Array.isArray(activeFilters.qualificationTags)) {
        setQualificationTagsFilter(activeFilters.qualificationTags);
      }

      setIsInitialLoad(false);
    }
  }, [
    projects,
    isInitialLoad,
    activeFilters.project,
    activeFilters.sales,
    activeFilters.salesId,
    activeFilters.status,
    activeFilters.closureDate,
    activeFilters.lastFollowUp,
    activeFilters.nextFollowUp,
    activeFilters.nextFollowUpFrom,
    activeFilters.nextFollowUpTo,
    activeFilters.createdOn,
    activeFilters.source,
    activeFilters.qualificationTags,
    variant,
    forcedProjectID,
    isClosedLostOnlyView
  ]);

  console.log({ leads, userId });

  const visibleLeads = useMemo(() => {
    const base = allLeads?.filter((item) => item?.status !== 'Closed') || [];
    if (!aiFilter || !isClientAiFilter(aiFilter)) return base;
    if (clientFilterLoading || clientFilterLeadIds === null) return [];
    return base.filter((lead) => clientFilterLeadIds.has(Number(lead.id)));
  }, [allLeads, aiFilter, clientFilterLeadIds, clientFilterLoading]);

  /** Keep paginating until every AI-matched lead is loaded, or the leads API is exhausted. */
  const effectiveHasMore = useMemo(() => {
    if (!isClientAiFilter(aiFilter) || clientFilterLoading || clientFilterLeadIds === null) {
      return hasMore;
    }
    if (clientFilterLeadIds.size === 0) return false;
    const foundCount = allLeads.filter((l) => clientFilterLeadIds.has(Number(l.id))).length;
    if (foundCount >= clientFilterLeadIds.size) return false;
    return hasMore;
  }, [aiFilter, clientFilterLoading, clientFilterLeadIds, allLeads, hasMore]);

  const handleChangeSecondaryTab = useCallback((tabName) => {
    setSecondaryTab(tabName);
  }, []);

  console.log('leads', leads);

  /* 
      Meta data
    */
  const leadsFormat = [
    'name',
    'project',
    'expected_clouser_date',
    'lead_source',
    'next_follow_up_date',
    'next_follow_up_agenda',
    'created_on',
    'assigned_to',
    'status',
    'qualification_tags',
    'dates',
    'phone_number',
    'unread_whatsapp_count',
    'last_whatsapp_preview',
    'last_whatsapp_message_at',
    'last_whatsapp_direction',
    'actions',
    'id'
  ];

  function formatDate(dateString) {
    if (!dateString) return '---';

    const date = new Date(dateString);

    if (isNaN(date.getTime())) return '---';

    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options); // e.g., 01 May 2025
  }

  const leadsData = useMemo(() => {
    return (
      visibleLeads?.map((el) => {
        const nextFollowUp = getNextFollowUpForLead(el?.follow_ups);

        return transformData(
          {
            ...el?.user,
            name:
              `${el?.user?.name || ''}${el?.reference_name ? ` (${el?.reference_name})` : ''}` ||
              '---',
            project: el?.projects?.[0]?.name || '---',
            expected_clouser_date: formatDate(el?.expected_clouser_date) || '---',
            assigned_to:
              el?.pstatus === 'Closed Lost' ? '' : el?.sales?.name || '---',
            id: el?.id,
            lead_source: getLeadSourceDisplay(el),
            next_follow_up_date: formatDate(nextFollowUp.date) || '---',
            next_follow_up_agenda: nextFollowUp.agenda || '---',
            created_on: formatDate(el?.created_at || el?.user?.created_at) || '---',
            status: stageDisplayName(el?.pstatus) || '---',
            qualification_tags: Array.isArray(el?.qualification_tags)
              ? el.qualification_tags
              : [],
            dates: el?.dates || [],
            phone_number: el?.user?.phone_number || '---',
            unread_whatsapp_count: Number(el?.unread_whatsapp_count) || 0,
            last_whatsapp_preview: el?.last_whatsapp_preview || '',
            last_whatsapp_message_at: el?.last_whatsapp_message_at || null,
            last_whatsapp_direction: el?.last_whatsapp_direction || '',
            actions: `Actions---${el?.id}---${el?.sales_id || ''}`,
            ai_score: el?.ai_score,
            ai_category: el?.ai_category
          },
          leadsFormat
        );
      }) || []
    );
  }, [visibleLeads]);

  const leadMetrics = useMemo(
    () => ({
      totalLeads: leadStatsCards?.data?.totalLead || 0,
      hotLeads: leadStatsCards?.data?.hotLead || 0,
      coldLeads: leadStatsCards?.data?.coldLead || 0,
      warmLeads: leadStatsCards?.data?.warmLead || 0,
      demoScheduledLeads: leadStatsCards?.data?.demoScheduledLead || 0,
      nurtureLeads: leadStatsCards?.data?.nurtureLead || 0,
      futureProspectLeads: leadStatsCards?.data?.futureProspectLead || 0,
      underConsideration: 0
    }),
    [leadStatsCards]
  );

  const handleMetricStatusFilter = useCallback(
    (statuses = []) => {
      const nextStatuses = normalizeStatusFilter(statuses);
      setPage(1);
      setpStatus(nextStatuses);
      setActiveFilters((prev) => {
        const newFilters = { ...prev, status: nextStatuses };
        try {
          localStorage.setItem('salesListFilters', JSON.stringify(newFilters));
          const params = buildSalesListFilterParams(
            new URLSearchParams(location.search),
            newFilters
          );
          const qs = params.toString();
          navigate(
            {
              pathname: location.pathname,
              search: qs ? `?${qs}` : ''
            },
            { replace: true, state: location.state }
          );
        } catch {
          // ignore URL sync failures
        }
        return newFilters;
      });
    },
    [setPage, location.search, location.pathname, location.state, navigate]
  );

  const metricCards = [
    {
      title: 'Add Lead',
      variant: 'addLead',
      onClick: () => setIsAddLeadActionModalOpen(true)
    },
    {
      title: 'Total Lead',
      value: leadMetrics.totalLeads,
      icon: <Users size={18} />,
      iconBgClass: 'bg-[#EEF2FF]',
      iconColorClass: 'text-[#4F46E5]',
      valueColorClass: 'text-[#3730A3]',
      borderClass: 'border-[#DDE3F8]',
      onClick: () => handleMetricStatusFilter([])
    },
    {
      title: 'Hot Lead',
      value: leadMetrics.hotLeads,
      icon: <Flame size={18} />,
      iconBgClass: 'bg-[#FFF1F2]',
      iconColorClass: 'text-[#E11D48]',
      valueColorClass: 'text-[#BE123C]',
      borderClass: 'border-[#FFD6DD]',
      onClick: () => handleMetricStatusFilter(['Hot'])
    },
    {
      title: 'Warm Lead',
      value: leadMetrics.warmLeads,
      icon: <Sun size={18} />,
      iconBgClass: 'bg-[#FEF9C3]',
      iconColorClass: 'text-[#A16207]',
      valueColorClass: 'text-[#A16207]',
      borderClass: 'border-[#FDE68A]',
      onClick: () => handleMetricStatusFilter(['Warm'])
    },
    {
      title: 'Cold Lead',
      value: leadMetrics.coldLeads,
      icon: <Snowflake size={18} />,
      iconBgClass: 'bg-[#E0F2FE]',
      iconColorClass: 'text-[#0284C7]',
      valueColorClass: 'text-[#0369A1]',
      borderClass: 'border-[#BAE6FD]',
      onClick: () => handleMetricStatusFilter(['Cold'])
    },
    {
      title: SITE_VISIT_SCHEDULED_LABEL,
      value: leadMetrics.demoScheduledLeads,
      icon: <MonitorPlay size={18} />,
      iconBgClass: 'bg-[#EEF2FF]',
      iconColorClass: 'text-[#4F46E5]',
      valueColorClass: 'text-[#3730A3]',
      borderClass: 'border-[#C7D2FE]',
      onClick: () => handleMetricStatusFilter(['Demo'])
    },
    {
      title: 'Nurture',
      value: leadMetrics.nurtureLeads,
      icon: <PauseCircle size={18} />,
      iconBgClass: 'bg-[#FFF7ED]',
      iconColorClass: 'text-[#C2410C]',
      valueColorClass: 'text-[#C2410C]',
      borderClass: 'border-[#FED7AA]',
      onClick: () => handleMetricStatusFilter(['Nurture'])
    },
    {
      title: 'Future Prospect',
      value: leadMetrics.futureProspectLeads,
      icon: <CalendarClock size={18} />,
      iconBgClass: 'bg-[#F0FDFA]',
      iconColorClass: 'text-[#0F766E]',
      valueColorClass: 'text-[#0F766E]',
      borderClass: 'border-[#99F6E4]',
      onClick: () => handleMetricStatusFilter(['Future Prospect'])
    }
  ];

  const handleToggleAddUser = () => {
    const willOpen = !openAddTargetModal;
    setOpenAddTargetModal(willOpen);

    // Track modal toggle
    mixpanel.track(willOpen ? 'Add Target Modal Opened' : 'Add Target Modal Closed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  };

  const handleSearchChange = useCallback(
    async (incomingQuery) => {
      setPage(1);
      setSearchQuery(incomingQuery);

      // Track search
      mixpanel.track('Sales List Search', {
        user_id: userId,
        search_query: incomingQuery,
        status: getStatusParam(pstatus) || 'all',
        project_id: projectID,
        timestamp: new Date().toISOString()
      });
    },
    [userId, pstatus, projectID]
  );

  const handleOnClickSearch = async () => {
    await refetchLeads();
  };

  // Load more data for infinite scroll
  const loadMore = useCallback(() => {
    if (effectiveHasMore && !isFetching && !isLoadingMore) {
      setIsLoadingMore(true);
      setPage((prevPage) => {
        console.log('Ã°Å¸â€œâ€ž Page updated from', prevPage, 'to', prevPage + 1);
        return prevPage + 1;
      });

      // Safety timeout to reset loading state
      setTimeout(() => {
        console.log('Ã¢ÂÂ° Safety timeout: resetting isLoadingMore');
        setIsLoadingMore(false);
      }, 10000); // 10 second timeout
    }
  }, [effectiveHasMore, isFetching, isLoadingMore, page, allLeads.length]);

  // Client AI filters: keep fetching lead pages until matches appear or API is done.
  useEffect(() => {
    if (!isClientAiFilter(aiFilter) || clientFilterLoading || !clientFilterLeadIds) return;
    if (!effectiveHasMore || isFetching || isLoadingMore) return;
    if (visibleLeads.length > 0) return;
    const foundCount = allLeads.filter((l) => clientFilterLeadIds.has(Number(l.id))).length;
    if (foundCount < clientFilterLeadIds.size) {
      loadMore();
    }
  }, [
    aiFilter,
    clientFilterLoading,
    clientFilterLeadIds,
    effectiveHasMore,
    isFetching,
    isLoadingMore,
    visibleLeads.length,
    allLeads,
    loadMore
  ]);

  const handleEdit = (id) => {
    const lead = allLeads?.find((lead) => lead?.id === parseInt(id));
    if (lead) {
      // Track edit modal open
      mixpanel.track('Edit Lead Modal Opened', {
        user_id: userId,
        lead_id: id,
        lead_name: lead?.user?.name,
        timestamp: new Date().toISOString()
      });

      setEditingLead(lead);
      setIsEditLeadModalOpen(true);

      // Prefill logic is now handled in EditLeadForm component
      // The editingLead prop will be passed to EditLeadForm for prefilling
    }
  };

  const handleClone = (id) => {
    const lead = allLeads?.find((lead) => lead?.id === parseInt(id));
    if (lead) {
      // Track clone modal open
      mixpanel.track('Duplicate Lead Modal Opened', {
        user_id: userId,
        lead_id: id,
        lead_name: lead?.user?.name,
        timestamp: new Date().toISOString()
      });

      setCloningLead(lead);
      setSelectedCloneProjects([]);
      setIsCloneLeadModalOpen(true);
    }
  };

  const { mutate: handleUpdateLeaUpdateLead } = useMutation({
    mutationFn: (id) => updateLead(id),
    onSuccess: async (result) => {
      toast.success('Lead updated successfully');
      await refetchLeads?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error updating lead');
    }
  });

  const handleTransfer = (id) => {
    const lead = allLeads?.find((lead) => lead?.id === parseInt(id));
    if (lead) {
      // Track update modal open
      mixpanel.track('Sales Update Modal Opened', {
        user_id: userId,
        lead_id: id,
        lead_name: lead?.name,
        current_sales_id: lead?.sales_id,
        timestamp: new Date().toISOString()
      });

      setSelectedLead({
        id: lead?.id,
        salesId: lead?.sales_id,
        projectId:
          lead?.interested_projects?.[0] ||
          lead?.projects?.[0]?.id ||
          projectID ||
          null
      });
      setIsUpdateModalOpen(true);
    }
  };

  const onSubmitCreate = (payload) => {
    console.log('Create Lead Payload:', payload);
    createLeadMutation?.(payload);
  };

  const onSubmitEdit = (payload) => {
    console.log('Edit Lead Payload:', payload);
    updateLeadMutation?.(payload);
  };

  const onSubmitClone = () => {
    if (selectedCloneProjects.length === 0) {
      toast.error('Please select at least one project');
      return;
    }

    const payload = {
      project_ids: selectedCloneProjects.map((project) => parseInt(project.id))
    };

    console.log('Duplicate Lead Payload:', payload);
    cloneLeadMutation(payload);
  };

  // Mutation for creating new lead
  const { mutate: createLeadMutation } = useMutation({
    mutationFn: (data) => createLead(data),
    onMutate: (data) => {
      mixpanel.track('Create Lead Started', {
        user_id: userId,
        project_ids: data.interested_projects,
        accommodations_count: data.interestedAccomodationType?.length || 0,
        plot_areas_count: data.plotArea?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result, variables) => {
      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        mixpanel.track('Create Lead Failed', {
          user_id: userId,
          error_message:
            result?.response?.data?.message || result?.message || 'Failed to create lead',
          project_ids: variables.interested_projects,
          timestamp: new Date().toISOString()
        });

        toast.error(result?.response?.data?.message || result?.message || 'Failed to create lead');
        return;
      }

      // Handle as success
      mixpanel.track('Create Lead Success', {
        user_id: userId,
        lead_id: result?.data?.id,
        project_ids: variables.interested_projects,
        timestamp: new Date().toISOString()
      });

      toast.success('Lead created successfully');
      setIsCreateLeadModalOpen(false);
      setPage(1);
      setAllLeads([]);
      setHasMore(true);
      setIsLoadingMore(false);
      queryClient.invalidateQueries({ queryKey: ['aiLeadsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['aiLeadsSummaryAll'] });

      // Force a fresh query with page 1
      const freshData = await getLeadsByStatusAndProject(
        projectID,
        status,
        effectivePstatus,
        1, // Force page 1
        limit,
        searchQuery,
        sales,
        closureDateFilter,
        lastFollowUpFilter,
        nextFollowUpFilter,
        createdOnFilter,
        sourceFilter,
        qualificationTagsFilter,
        createdFromFilter,
        createdToFilter,
        nextFollowUpFromFilter,
        nextFollowUpToFilter
      );

      if (freshData?.data) {
        setAllLeads(freshData.data);
        const totalLeads = freshData?.meta?.total || 0;
        const isLastPage = freshData.data.length < limit;
        const hasMoreData = !isLastPage && freshData.data.length < totalLeads;
        setHasMore(hasMoreData);
      }
    },
    onError: (error, variables) => {
      mixpanel.track('Create Lead Failed', {
        user_id: userId,
        error_message: error?.response?.data?.message || error?.message || 'Failed to create lead',
        project_ids: variables.interested_projects,
        timestamp: new Date().toISOString()
      });

      toast.error(error?.response?.data?.message || error?.message || 'Failed to create lead');
    }
  });

  // Mutation for updating lead
  const { mutate: updateLeadMutation } = useMutation({
    mutationFn: (data) => updateLead(editingLead?.id, data),
    onMutate: (data) => {
      mixpanel.track('Update Lead Started', {
        user_id: userId,
        lead_id: editingLead?.id,
        project_ids: data.interested_projects,
        accommodations_count: data.interestedAccomodationType?.length || 0,
        plot_areas_count: data.plotArea?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result, variables) => {
      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        mixpanel.track('Update Lead Failed', {
          user_id: userId,
          error_message:
            result?.response?.data?.message || result?.message || 'Failed to update lead',
          lead_id: editingLead?.id,
          project_ids: variables.interested_projects,
          timestamp: new Date().toISOString()
        });

        toast.error(result?.response?.data?.message || result?.message || 'Failed to update lead');
        return;
      }

      // Handle as success
      mixpanel.track('Update Lead Success', {
        user_id: userId,
        lead_id: editingLead?.id,
        project_ids: variables.interested_projects,
        timestamp: new Date().toISOString()
      });

      toast.success('Lead updated successfully');
      setIsEditLeadModalOpen(false);
      setEditingLead(null);

      setPage(1);
      setAllLeads([]);
      setHasMore(true);
      setIsLoadingMore(false);

      // Force a fresh query with page 1
      const freshData = await getLeadsByStatusAndProject(
        projectID,
        status,
        effectivePstatus,
        1, // Force page 1
        limit,
        searchQuery,
        sales,
        closureDateFilter,
        lastFollowUpFilter,
        nextFollowUpFilter,
        createdOnFilter,
        sourceFilter,
        qualificationTagsFilter,
        createdFromFilter,
        createdToFilter,
        nextFollowUpFromFilter,
        nextFollowUpToFilter
      );

      if (freshData?.data) {
        setAllLeads(freshData.data);
        const totalLeads = freshData?.meta?.total || 0;
        const isLastPage = freshData.data.length < limit;
        const hasMoreData = !isLastPage && freshData.data.length < totalLeads;
        setHasMore(hasMoreData);
      }

      // Form reset is now handled in EditLeadForm component
    },
    onError: (error, variables) => {
      mixpanel.track('Update Lead Failed', {
        user_id: userId,
        error_message: error?.response?.data?.message || error?.message || 'Failed to update lead',
        lead_id: editingLead?.id,
        project_ids: variables.interested_projects,
        timestamp: new Date().toISOString()
      });

      toast.error(error?.response?.data?.message || error?.message || 'Failed to update lead');
    }
  });

  // Mutation for cloning lead
  const { mutate: cloneLeadMutation } = useMutation({
    mutationFn: (payload) => cloneLead(cloningLead?.id, payload),
    onMutate: (data) => {
      mixpanel.track('Duplicate Lead Started', {
        user_id: userId,
        lead_id: cloningLead?.id,
        project_ids: data.project_ids,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result, variables) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        mixpanel.track('Duplicate Lead Failed', {
          user_id: userId,
          error_message:
            result?.response?.data?.message || result?.message || 'Failed to duplicate lead',
          lead_id: cloningLead?.id,
          project_ids: variables.project_ids,
          timestamp: new Date().toISOString()
        });

        toast.error(
          result?.response?.data?.message || result?.message || 'Failed to duplicate lead'
        );
        return;
      }

      // Handle as success
      mixpanel.track('Duplicate Lead Success', {
        user_id: userId,
        lead_id: cloningLead?.id,
        project_ids: variables.project_ids,
        timestamp: new Date().toISOString()
      });

      toast.success('Lead cloned successfully');
      setIsCloneLeadModalOpen(false);
      setCloningLead(null);
      setSelectedCloneProjects([]);

      setPage(1);
      setAllLeads([]);
      setHasMore(true);
      setIsLoadingMore(false);

      // Force a fresh query with page 1
      const freshData = await getLeadsByStatusAndProject(
        projectID,
        status,
        effectivePstatus,
        1, // Force page 1
        limit,
        searchQuery,
        sales,
        closureDateFilter,
        lastFollowUpFilter,
        nextFollowUpFilter,
        createdOnFilter,
        sourceFilter,
        qualificationTagsFilter,
        createdFromFilter,
        createdToFilter,
        nextFollowUpFromFilter,
        nextFollowUpToFilter
      );

      if (freshData?.data) {
        setAllLeads(freshData.data);
        const totalLeads = freshData?.meta?.total || 0;
        const isLastPage = freshData.data.length < limit;
        const hasMoreData = !isLastPage && freshData.data.length < totalLeads;
        setHasMore(hasMoreData);
      }
    },
    onError: (error, variables) => {
      mixpanel.track('Duplicate Lead Failed', {
        user_id: userId,
        error_message:
          error?.response?.data?.message || error?.message || 'Failed to duplicate lead',
        lead_id: cloningLead?.id,
        project_ids: variables.project_ids,
        timestamp: new Date().toISOString()
      });

      toast.error(error?.response?.data?.message || error?.message || 'Failed to duplicate lead');
    }
  });

  const transformedDataProjects = useMemo(() => {
    return projects?.map((p) => ({
      id: p.id,
      name: p.name
    }));
  }, [projects]);

  const projectHeaderRef = React.useRef(null);
  const [projectDropdownRect, setProjectDropdownRect] = useState(null);
  const [salesDropdownRect, setSalesDropdownRect] = useState(null);
  const salesHeaderRef = React.useRef(null);
  const statusHeaderRef = React.useRef(null);
  const [statusDropdownRect, setStatusDropdownRect] = useState(null);
  const closureDateHeaderRef = React.useRef(null);
  const [closureDateDropdownRect, setClosureDateDropdownRect] = useState(null);
  const lastFollowUpHeaderRef = React.useRef(null);
  const [lastFollowUpDropdownRect, setLastFollowUpDropdownRect] = useState(null);
  const nextFollowUpHeaderRef = React.useRef(null);
  const [nextFollowUpDropdownRect, setNextFollowUpDropdownRect] = useState(null);
  const createdOnHeaderRef = React.useRef(null);
  const [createdOnDropdownRect, setCreatedOnDropdownRect] = useState(null);
  const sourceHeaderRef = React.useRef(null);

  const leadSourceOptions = LEAD_SOURCE_FILTER_OPTIONS;

  // Status options for filter
  const statusOptions = [
    { id: 'Cold', label: 'Cold', value: 'Cold' },
    { id: 'Demo', label: SITE_VISIT_SCHEDULED_LABEL, value: 'Demo' },
    { id: 'Warm', label: 'Warm', value: 'Warm' },
    { id: 'Hot', label: 'Hot', value: 'Hot' },
    { id: 'Nurture', label: 'Paused / Nurture', value: 'Nurture' },
    { id: 'Future Prospect', label: 'Future Prospect', value: 'Future Prospect' }
  ];

  // Closure date options for filter
  const closureDateOptions = [
    { id: 'closest_closure_date', label: 'Closest', value: 'closest_closure_date' },
    { id: 'furthest_closure_date', label: 'Furthest', value: 'furthest_closure_date' }
  ];

  const lastFollowUpOptions = [
    { id: 'last_follow_up_date_desc', label: 'Closest', value: 'last_follow_up_date_desc' },
    { id: 'last_follow_up_date_asc', label: 'Furthest', value: 'last_follow_up_date_asc' }
  ];

  const nextFollowUpOptions = [
    { id: 'today', label: 'Today', value: 'today' },
    { id: 'next_follow_up_date_desc', label: 'Closest', value: 'next_follow_up_date_desc' },
    { id: 'next_follow_up_date_asc', label: 'Furthest', value: 'next_follow_up_date_asc' }
  ];

  const createdOnOptions = [
    { id: 'created_on_desc', label: 'Closest', value: 'created_on_desc' },
    { id: 'created_on_asc', label: 'Furthest', value: 'created_on_asc' }
  ];

  // Handler to open dropdown and set position
  const handleHeaderDropdownClick = (type, event) => {
    event.preventDefault();
    event.stopPropagation();

    console.log('Dropdown click:', { type, openDropdown, event });

    // If clicking the same dropdown that's already open, close it
    if (openDropdown === type) {
      console.log('Closing same dropdown');
      setOpenDropdown(null);
      return;
    }

    if (type === 'project' && projectHeaderRef?.current) {
      setProjectDropdownRect(projectHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'assigned' && salesHeaderRef?.current) {
      setSalesDropdownRect(salesHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'status' && statusHeaderRef?.current) {
      setStatusDropdownRect(statusHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'closureDate' && closureDateHeaderRef?.current) {
      setClosureDateDropdownRect(closureDateHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'lastFollowUp' && lastFollowUpHeaderRef?.current) {
      setLastFollowUpDropdownRect(lastFollowUpHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'nextFollowUp' && nextFollowUpHeaderRef?.current) {
      setNextFollowUpDropdownRect(nextFollowUpHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'createdOn' && createdOnHeaderRef?.current) {
      setCreatedOnDropdownRect(createdOnHeaderRef?.current?.getBoundingClientRect());
    }
    setOpenDropdown(type);
  };

  // Handler to close dropdown
  const handleCloseDropdown = () => setOpenDropdown(null);

  // Handler to select item (can be extended to filter, etc.)
  const handleSelectDropdownItem = (type, value, keepDropdownOpen = false) => {
    console.log('Selecting dropdown item:', { type, value });
    setOpenDropdown(keepDropdownOpen ? type : null);

    // Track filter changes
    if (type === 'project') {
      if (!value) {
        // Clear project filter
        mixpanel.track('Sales List Filter Cleared', {
          user_id: userId,
          filter_type: 'project',
          timestamp: new Date().toISOString()
        });
        setProject('');
        const newFilters = { ...activeFilters, project: null };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Sales List Filter Changed', {
          user_id: userId,
          filter_type: 'project',
          project_id: value,
          timestamp: new Date().toISOString()
        });
        setProject(value);

        // Update active filters
        const selectedProject = projectsForDropdown?.find((p) => p.id === value);
        const newFilters = {
          ...activeFilters,
          project: selectedProject?.label || null
        };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      }
    } else if (type === 'assigned') {
      if (!value) {
        // Clear sales filter
        mixpanel.track('Sales List Filter Cleared', {
          user_id: userId,
          filter_type: 'assigned_sales',
          timestamp: new Date().toISOString()
        });
        setSales(null);
        const newFilters = { ...activeFilters, sales: null, salesId: null };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Sales List Filter Changed', {
          user_id: userId,
          filter_type: 'assigned_sales',
          sales_id: value,
          timestamp: new Date().toISOString()
        });
        setSales(value);

        // Update active filters
        const selectedSales = salesVerticalUsers.find((s) => s.id === value);
        const newFilters = {
          ...activeFilters,
          sales: selectedSales?.name || null,
          salesId: value
        };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      }
    } else if (type === 'status') {
      const updatedStatuses =
        value === 'all'
          ? []
          : pstatus.includes(value)
            ? pstatus.filter((selectedStatus) => selectedStatus !== value)
            : [...pstatus, value];

      mixpanel.track('Sales List Filter Changed', {
        user_id: userId,
        filter_type: 'status',
        status: getStatusParam(updatedStatuses) || 'all',
        selected_statuses: updatedStatuses,
        timestamp: new Date().toISOString()
      });
      setpStatus(updatedStatuses);
      const newFilters = { ...activeFilters, status: updatedStatuses };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (type === 'closureDate') {
      if (!value) {
        // Clear closure date filter
        mixpanel.track('Sales List Filter Cleared', {
          user_id: userId,
          filter_type: 'closure_date',
          timestamp: new Date().toISOString()
        });
        setClosureDateFilter(null);
        const newFilters = { ...activeFilters, closureDate: null };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Sales List Filter Changed', {
          user_id: userId,
          filter_type: 'closure_date',
          closure_date: value,
          timestamp: new Date().toISOString()
        });
        setClosureDateFilter(value);
        const newFilters = { ...activeFilters, closureDate: value };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      }
    } else if (type === 'lastFollowUp') {
      if (!value) {
        // Clear last follow up filter
        mixpanel.track('Sales List Filter Cleared', {
          user_id: userId,
          filter_type: 'last_follow_up',
          timestamp: new Date().toISOString()
        });
        setLastFollowUpFilter(null);
        const newFilters = { ...activeFilters, lastFollowUp: null };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Sales List Filter Changed', {
          user_id: userId,
          filter_type: 'last_follow_up',
          last_follow_up: value,
          timestamp: new Date().toISOString()
        });
        setLastFollowUpFilter(value);
        const newFilters = { ...activeFilters, lastFollowUp: value };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      }
    } else if (type === 'nextFollowUp') {
      if (!value) {
        // Clear next follow up filter
        mixpanel.track('Sales List Filter Cleared', {
          user_id: userId,
          filter_type: 'next_follow_up',
          timestamp: new Date().toISOString()
        });
        setNextFollowUpFilter(null);
        setNextFollowUpFromFilter(null);
        setNextFollowUpToFilter(null);
        const newFilters = {
          ...activeFilters,
          nextFollowUp: null,
          nextFollowUpFrom: null,
          nextFollowUpTo: null
        };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else if (value === 'custom') {
        setNextFollowUpFilter('custom');
        setOpenDropdown('nextFollowUp');
        const newFilters = { ...activeFilters, nextFollowUp: 'custom' };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Sales List Filter Changed', {
          user_id: userId,
          filter_type: 'next_follow_up',
          next_follow_up: value,
          timestamp: new Date().toISOString()
        });
        setNextFollowUpFilter(value);
        setNextFollowUpFromFilter(null);
        setNextFollowUpToFilter(null);
        const newFilters = {
          ...activeFilters,
          nextFollowUp: value,
          nextFollowUpFrom: null,
          nextFollowUpTo: null
        };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      }
    } else if (type === 'createdOn') {
      if (!value) {
        // Clear created on filter
        mixpanel.track('Sales List Filter Cleared', {
          user_id: userId,
          filter_type: 'created_on',
          timestamp: new Date().toISOString()
        });
        setCreatedOnFilter(null);
        setCreatedFromFilter(null);
        setCreatedToFilter(null);
        const newFilters = {
          ...activeFilters,
          createdOn: null,
          createdFrom: null,
          createdTo: null
        };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else if (value === 'custom') {
        // Keep dropdown open for custom range inputs
        setCreatedOnFilter('custom');
        setOpenDropdown('createdOn');
        const newFilters = { ...activeFilters, createdOn: 'custom' };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Sales List Filter Changed', {
          user_id: userId,
          filter_type: 'created_on',
          created_on: value,
          timestamp: new Date().toISOString()
        });
        setCreatedOnFilter(value);
        setCreatedFromFilter(null);
        setCreatedToFilter(null);
        const newFilters = {
          ...activeFilters,
          createdOn: value,
          createdFrom: null,
          createdTo: null
        };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      }
    } else if (type === 'source') {
      if (!value) {
        mixpanel.track('Sales List Filter Cleared', {
          user_id: userId,
          filter_type: 'source',
          timestamp: new Date().toISOString()
        });
        setSourceFilter(null);
        const newFilters = { ...activeFilters, source: null };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Sales List Filter Changed', {
          user_id: userId,
          filter_type: 'source',
          source: value,
          timestamp: new Date().toISOString()
        });
        setSourceFilter(value);
        const newFilters = { ...activeFilters, source: value };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      }
    } else if (type === 'qualificationTag') {
      if (!value) {
        setQualificationTagsFilter([]);
        const newFilters = { ...activeFilters, qualificationTags: [] };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        setQualificationTagsFilter((prev) => {
          const next = prev.includes(value)
            ? prev.filter((t) => t !== value)
            : [...prev, value];
          const newFilters = { ...activeFilters, qualificationTags: next };
          setActiveFilters(newFilters);
          saveFiltersToStorage(newFilters);
          return next;
        });
      }
    }
  };

  // Add a global click handler to close dropdown when clicking outside
  useEffect(() => {
    if (!openDropdown && !datesPopover) return;

    const handleClick = (event) => {
      // Don't close if clicking inside a dropdown or on dropdown trigger
      if (
        event.target.closest('[data-dropdown-trigger]') ||
        event.target.closest('[data-dropdown-menu]') ||
        event.target.closest('.absolute.bg-white.border.rounded.shadow')
      ) {
        return;
      }
      setOpenDropdown(null);
      setDatesPopover(null);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [openDropdown, datesPopover]);

  const PortalDropdown = ({ anchorRect, children, minWidth }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        data-dropdown-menu
        className="bg-white border rounded shadow z-[2000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom + 4,
          minWidth: minWidth || Math.max(anchorRect.width, 180),
          maxHeight: '320px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  const applyCreatedOnCustomRange = () => {
    if (!createdFromFilter || !createdToFilter) {
      toast.error('Select both from and to dates');
      return;
    }
    if (createdFromFilter > createdToFilter) {
      toast.error('From date must be before to date');
      return;
    }
    setCreatedOnFilter('custom');
    const newFilters = {
      ...activeFilters,
      createdOn: 'custom',
      createdFrom: createdFromFilter,
      createdTo: createdToFilter
    };
    setActiveFilters(newFilters);
    saveFiltersToStorage(newFilters);
    setOpenDropdown(null);
    mixpanel.track('Sales List Filter Changed', {
      user_id: userId,
      filter_type: 'created_on_custom',
      created_from: createdFromFilter,
      created_to: createdToFilter,
      timestamp: new Date().toISOString()
    });
  };

  const applyNextFollowUpCustomRange = () => {
    if (!nextFollowUpFromFilter || !nextFollowUpToFilter) {
      toast.error('Select both from and to dates');
      return;
    }
    if (nextFollowUpFromFilter > nextFollowUpToFilter) {
      toast.error('From date must be before to date');
      return;
    }
    setNextFollowUpFilter('custom');
    const newFilters = {
      ...activeFilters,
      nextFollowUp: 'custom',
      nextFollowUpFrom: nextFollowUpFromFilter,
      nextFollowUpTo: nextFollowUpToFilter
    };
    setActiveFilters(newFilters);
    saveFiltersToStorage(newFilters);
    setOpenDropdown(null);
    mixpanel.track('Sales List Filter Changed', {
      user_id: userId,
      filter_type: 'next_follow_up_custom',
      next_follow_up_from: nextFollowUpFromFilter,
      next_follow_up_to: nextFollowUpToFilter,
      timestamp: new Date().toISOString()
    });
  };

  // Function to save filters to localStorage + URL
  const saveFiltersToStorage = (filters) => {
    localStorage.setItem('salesListFilters', JSON.stringify(filters));
    try {
      const params = buildSalesListFilterParams(
        new URLSearchParams(location.search),
        filters
      );
      // Preserve non-filter params (aiFilter, view, tab, projectID, returnTo, etc.)
      const qs = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: qs ? `?${qs}` : ''
        },
        { replace: true, state: location.state }
      );
    } catch {
      // ignore URL sync failures
    }
  };

  const removeStatusFilter = (statusValue) => {
    const updatedStatuses = pstatus.filter((selectedStatus) => selectedStatus !== statusValue);
    setpStatus(updatedStatuses);
    const newFilters = { ...activeFilters, status: updatedStatuses };
    setActiveFilters(newFilters);
    saveFiltersToStorage(newFilters);
  };

  // Function to clear specific filter
  const clearFilter = (filterType) => {
    if (filterType === 'project') {
      setProject('');
      const newFilters = { ...activeFilters, project: null };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'sales') {
      setSales(null);
      const newFilters = { ...activeFilters, sales: null, salesId: null };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'status') {
      setpStatus([]); // Reset to default
      const newFilters = { ...activeFilters, status: [] };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'closureDate') {
      setClosureDateFilter(null);
      const newFilters = { ...activeFilters, closureDate: null };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'lastFollowUp') {
      setLastFollowUpFilter(null);
      const newFilters = { ...activeFilters, lastFollowUp: null };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'nextFollowUp') {
      setNextFollowUpFilter(null);
      setNextFollowUpFromFilter(null);
      setNextFollowUpToFilter(null);
      const newFilters = {
        ...activeFilters,
        nextFollowUp: null,
        nextFollowUpFrom: null,
        nextFollowUpTo: null
      };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'createdOn') {
      setCreatedOnFilter(null);
      setCreatedFromFilter(null);
      setCreatedToFilter(null);
      const newFilters = {
        ...activeFilters,
        createdOn: null,
        createdFrom: null,
        createdTo: null
      };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'source') {
      setSourceFilter(null);
      const newFilters = { ...activeFilters, source: null };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'qualificationTags') {
      setQualificationTagsFilter([]);
      const newFilters = { ...activeFilters, qualificationTags: [] };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    }

    // Track filter clear
    mixpanel.track('Sales List Filter Cleared', {
      user_id: userId,
      filter_type: filterType,
      timestamp: new Date().toISOString()
    });
  };

  // Form logic is now handled in AddLeadForm and EditLeadForm components

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckLeadDelete(id),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (result, id) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error checking dependencies', result);
        toast.error(
          result?.response?.data?.message || result?.message || 'Error checking dependencies'
        );
        return;
      }

      // Handle as success
      const lead = leadsData?.find((item) => parseInt(item.id) === parseInt(id));
      const leadName = lead?.name || 'Lead';
      if (result.canDelete) {
        setCanDelete('yes');
        setResData({ name: leadName });
        setSelectedIds([parseInt(id)]);
      } else {
        // Fallback if not found
        setResData({ ...result, name: leadName });
        setCanDelete('no');
        setSelectedIds([parseInt(id)]);
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error checking dependencies', err);
      toast.error('Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteLead(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (result, variables) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error deleting lead', result);
        toast.error(result?.response?.data?.message || result?.message || 'Error deleting lead');
        return;
      }

      // Handle as success
      const deletedIds = (Array.isArray(variables?.ids) ? variables.ids : []).map(Number);
      const deletedIdSet = new Set(deletedIds);
      const deletedCount = deletedIds.length || 1;
      toast.success(
        deletedCount > 1 ? `${deletedCount} leads deleted successfully` : 'Lead Deleted Successfully'
      );
      setSelectedIds([]);
      setSelectedItems([]);
      setCanDelete(null);
      setIsBulk(false);
      setSelectAll(false);
      setDeleteBulkMode(false);
      setDeleteSelectedIds([]);

      // Drop deleted rows immediately so they can't be opened as ghosts.
      setAllLeads((prev) => prev.filter((lead) => !deletedIdSet.has(Number(lead.id))));
      setPage(1);
      setHasMore(true);
      setIsLoadingMore(false);

      // Stale React Query pages were re-applied by the leads→allLeads sync effect
      // after delete, so ghost leads stayed visible with "Lead not found" on open.
      await queryClient.cancelQueries({ queryKey: ['lead'] });
      queryClient.removeQueries({ queryKey: ['lead'] });
      queryClient.invalidateQueries({ queryKey: ['leadStatsCards'] });

      const freshData = await getLeadsByStatusAndProject(
        projectID,
        status,
        effectivePstatus,
        1, // Force page 1
        limit,
        searchQuery,
        sales,
        closureDateFilter,
        lastFollowUpFilter,
        nextFollowUpFilter,
        createdOnFilter,
        sourceFilter,
        qualificationTagsFilter,
        createdFromFilter,
        createdToFilter,
        nextFollowUpFromFilter,
        nextFollowUpToFilter
      );

      if (freshData?.data) {
        setAllLeads(freshData.data);
        queryClient.setQueryData(
          [
            'lead',
            1,
            status,
            projectID,
            effectivePstatus,
            searchQuery,
            sales,
            closureDateFilter,
            lastFollowUpFilter,
            nextFollowUpFilter,
            nextFollowUpFromFilter,
            nextFollowUpToFilter,
            createdOnFilter,
            createdFromFilter,
            createdToFilter,
            sourceFilter,
            qualificationTagsFilter
          ],
          freshData
        );
        const totalLeads = freshData?.meta?.total || 0;
        const isLastPage = freshData.data.length < limit;
        const hasMoreData = !isLastPage && freshData.data.length < totalLeads;
        setHasMore(hasMoreData);
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error deleting lead', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting lead');
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  const handleSelectAll = async (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (checked) {
      setIsSubmitting(true); // Start loader

      try {
        const promises = leadsData?.map((item) =>
          handleCheckLeadDelete(item.id)
            .then((res) => {
              if (res.canDelete) return item.id;
              return null;
            })
            .catch(() => null)
        );

        const results = await Promise.all(promises);
        const validIds = results.filter((id) => id !== null);
        setSelectedItems(validIds);
      } finally {
        setIsSubmitting(false); // Stop loader
      }
    } else {
      setSelectedItems([]);
    }
  };

  const HandleFunctionCalls = async (id, checked) => {
    if (checked) {
      setIsSubmitting(true);
      try {
        const res = await handleCheckLeadDelete(id);
        if (res.canDelete) {
          setIsSubmitting(false);
          setSelectedItems((prev) => [...prev, id]);
        } else if (!res.canDelete) {
          setIsSubmitting(false);
          toast.error(`Can not delete this lead because there is dependencies`);
        }
      } catch (err) {
        setIsSubmitting(false);
        console.error('Error checking dependencies', err);
        toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
      }
    } else {
      setIsSubmitting(false);
      setSelectedItems((prev) => prev.filter((item) => item !== id));
    }
  };

  // ----- Bulk WhatsApp flow send helpers -----
  const getLeadProjectId = (leadId) => {
    const rawLead = allLeads?.find((l) => l?.id === leadId);
    return rawLead?.projects?.[0]?.id ?? null;
  };

  const toggleWaLead = (leadId, checked) => {
    setWaSelectedLeadIds((prev) => {
      if (!checked) return prev.filter((id) => id !== leadId);
      if (prev.includes(leadId)) return prev;
      if (prev.length >= WA_MAX_LEADS) {
        toast.error(`You can select up to ${WA_MAX_LEADS} leads at a time.`);
        return prev;
      }
      return [...prev, leadId];
    });
  };

  // Lead ids on the current page that can receive a WhatsApp flow (have a phone).
  const waSelectableLeadIds = useMemo(
    () =>
      (leadsData || [])
        .filter((lead) => lead?.phone_number && lead.phone_number !== '---')
        .map((lead) => lead?.id),
    [leadsData]
  );

  const waSelectAllChecked =
    waSelectableLeadIds.length > 0 &&
    waSelectableLeadIds.slice(0, WA_MAX_LEADS).every((id) => waSelectedLeadIds.includes(id));

  const handleWaSelectAll = (checked) => {
    if (!checked) {
      // Only deselect leads on the current page; keep selections from other pages.
      setWaSelectedLeadIds((prev) =>
        prev.filter((id) => !waSelectableLeadIds.includes(id))
      );
      return;
    }
    const pageIds = waSelectableLeadIds;
    setWaSelectedLeadIds((prev) => {
      const next = [...prev];
      for (const id of pageIds) {
        if (next.includes(id)) continue;
        if (next.length >= WA_MAX_LEADS) break;
        next.push(id);
      }
      return next;
    });
    const alreadySelectedOnPage = pageIds.filter((id) => waSelectedLeadIds.includes(id)).length;
    const remainingSlots = Math.max(0, WA_MAX_LEADS - waSelectedLeadIds.length);
    const newFromPage = pageIds.length - alreadySelectedOnPage;
    if (newFromPage > remainingSlots) {
      toast.error(`You can select up to ${WA_MAX_LEADS} leads at a time.`);
    }
  };

  const exitWaBulkMode = () => {
    setWaBulkMode(false);
    setWaSelectedLeadIds([]);
    setWaFlowPickerOpen(false);
  };

  const TRANSFER_MAX_LEADS = 100;

  const toggleTransferLead = (leadId, checked) => {
    setTransferSelectedIds((prev) => {
      if (!checked) return prev.filter((id) => id !== leadId);
      if (prev.includes(leadId)) return prev;
      if (prev.length >= TRANSFER_MAX_LEADS) {
        toast.error(`You can select up to ${TRANSFER_MAX_LEADS} leads at a time.`);
        return prev;
      }
      return [...prev, leadId];
    });
  };

  const transferSelectableLeadIds = useMemo(
    () => (leadsData || []).map((lead) => lead?.id).filter(Boolean),
    [leadsData]
  );

  const transferSelectAllChecked =
    transferSelectableLeadIds.length > 0 &&
    transferSelectableLeadIds
      .slice(0, TRANSFER_MAX_LEADS)
      .every((id) => transferSelectedIds.includes(id));

  const handleTransferSelectAll = (checked) => {
    if (!checked) {
      setTransferSelectedIds((prev) =>
        prev.filter((id) => !transferSelectableLeadIds.includes(id))
      );
      return;
    }
    setTransferSelectedIds((prev) => {
      const next = [...prev];
      for (const id of transferSelectableLeadIds) {
        if (next.includes(id)) continue;
        if (next.length >= TRANSFER_MAX_LEADS) break;
        next.push(id);
      }
      return next;
    });
  };

  const exitTransferBulkMode = () => {
    setTransferBulkMode(false);
    setTransferSelectedIds([]);
    setIsBulkTransferModalOpen(false);
  };

  const DELETE_MAX_LEADS = 100;

  const toggleDeleteLead = (leadId, checked) => {
    setDeleteSelectedIds((prev) => {
      if (!checked) return prev.filter((id) => id !== leadId);
      if (prev.includes(leadId)) return prev;
      if (prev.length >= DELETE_MAX_LEADS) {
        toast.error(`You can select up to ${DELETE_MAX_LEADS} leads at a time.`);
        return prev;
      }
      return [...prev, leadId];
    });
  };

  const deleteSelectableLeadIds = useMemo(
    () => (leadsData || []).map((lead) => lead?.id).filter(Boolean),
    [leadsData]
  );

  const deleteSelectAllChecked =
    deleteSelectableLeadIds.length > 0 &&
    deleteSelectableLeadIds
      .slice(0, DELETE_MAX_LEADS)
      .every((id) => deleteSelectedIds.includes(id));

  const handleDeleteSelectAll = (checked) => {
    if (!checked) {
      setDeleteSelectedIds((prev) =>
        prev.filter((id) => !deleteSelectableLeadIds.includes(id))
      );
      return;
    }
    setDeleteSelectedIds((prev) => {
      const next = [...prev];
      for (const id of deleteSelectableLeadIds) {
        if (next.includes(id)) continue;
        if (next.length >= DELETE_MAX_LEADS) break;
        next.push(id);
      }
      return next;
    });
  };

  const exitDeleteBulkMode = () => {
    setDeleteBulkMode(false);
    setDeleteSelectedIds([]);
  };

  const handleConfirmBulkDelete = () => {
    if (deleteSelectedIds.length === 0) {
      toast.error('Select at least one lead to delete.');
      return;
    }
    setSelectedIds(deleteSelectedIds);
    setResData({
      name:
        deleteSelectedIds.length === 1
          ? leadsData?.find((l) => l.id === deleteSelectedIds[0])?.name || 'Lead'
          : `${deleteSelectedIds.length} leads`
    });
    setCanDelete('yes');
  };

  const transferSelectedProjectIds = useMemo(() => {
    const ids = new Set();
    transferSelectedIds.forEach((leadId) => {
      const pid = getLeadProjectId(leadId);
      if (pid) ids.add(pid);
    });
    return [...ids];
  }, [transferSelectedIds, allLeads]);

  const transferResolvedProjectId =
    transferSelectedProjectIds.length === 1
      ? transferSelectedProjectIds[0]
      : projectID || null;

  const handleOpenBulkTransferModal = () => {
    if (transferSelectedIds.length === 0) {
      toast.error('Select at least one lead to transfer.');
      return;
    }
    if (transferSelectedProjectIds.length > 1) {
      toast.error(
        'Selected leads belong to different projects. Please select leads from a single project.'
      );
      return;
    }
    if (!transferResolvedProjectId && transferSelectedProjectIds.length === 0) {
      toast.error('Selected leads need a project to transfer.');
      return;
    }
    setIsBulkTransferModalOpen(true);
  };

  const refreshLeadsAfterTransfer = async () => {
    setPage(1);
    setAllLeads([]);
    setHasMore(true);
    setIsLoadingMore(false);
    const freshData = await getLeadsByStatusAndProject(
      projectID,
      status,
      effectivePstatus,
      1,
      limit,
      searchQuery,
      sales,
      closureDateFilter,
      lastFollowUpFilter,
      nextFollowUpFilter,
      createdOnFilter,
      sourceFilter,
      qualificationTagsFilter,
      createdFromFilter,
      createdToFilter,
      nextFollowUpFromFilter,
      nextFollowUpToFilter
    );
    if (freshData?.data) {
      setAllLeads(freshData.data);
      const totalLeads = freshData?.meta?.total || 0;
      const isLastPage = freshData.data.length < limit;
      const hasMoreData = !isLastPage && freshData.data.length < totalLeads;
      setHasMore(hasMoreData);
    }
  };

  // Distinct project ids among the selected leads (leads without a project are ignored).
  const waSelectedProjectIds = useMemo(() => {
    const ids = new Set();
    waSelectedLeadIds.forEach((leadId) => {
      const pid = getLeadProjectId(leadId);
      if (pid) ids.add(pid);
    });
    return [...ids];
  }, [waSelectedLeadIds, allLeads]);

  const waResolvedProjectId =
    waSelectedProjectIds.length === 1 ? waSelectedProjectIds[0] : projectID || null;

  const handleOpenWaFlowPicker = () => {
    if (waSelectedLeadIds.length === 0) {
      toast.error('Select at least one lead to send a flow.');
      return;
    }
    if (waSelectedProjectIds.length > 1) {
      toast.error(
        'Selected leads belong to different projects. Please select leads from a single project.'
      );
      return;
    }
    if (!waResolvedProjectId) {
      toast.error('Could not determine the project for the selected leads.');
      return;
    }
    setWaFlowPickerOpen(true);
  };

  // Bulk WhatsApp flow send is only available within a single project. If the
  // project filter is cleared, leave selection mode to avoid a stuck state.
  useEffect(() => {
    if (!projectID && waBulkMode) {
      exitWaBulkMode();
    }
  }, [projectID, waBulkMode]);

  // Mutation for creating duplicate leads
  const { mutate: createDuplicateLeadsMutation } = useMutation({
    mutationFn: (payload) => createDuplicateLeads(payload),
    onMutate: () => {
      setIsProcessingDuplicates(true);
      mixpanel.track('Create Duplicate Leads Started', {
        user_id: userId,
        duplicates_count: selectedDuplicates.length,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result, variables) => {
      setIsProcessingDuplicates(false);

      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Create Duplicate Leads Failed', {
          user_id: userId,
          error_message:
            result?.response?.data?.message ||
            result?.message ||
            'Failed to create duplicate leads',
          duplicates_count: variables.duplicates.length,
          timestamp: new Date().toISOString()
        });
        toast.error(
          result?.response?.data?.message || result?.message || 'Failed to create duplicate leads'
        );
        return;
      }

      mixpanel.track('Create Duplicate Leads Success', {
        user_id: userId,
        duplicates_count: variables.duplicates.length,
        timestamp: new Date().toISOString()
      });

      toast.success('Duplicate leads created successfully');

      // Remove accepted duplicates from the list
      // Use variables.duplicates instead of selectedDuplicates state
      const acceptedDuplicates = variables.duplicates || [];
      const remainingDuplicates = uploadDuplicates.filter(
        (duplicate) => !acceptedDuplicates.some((accepted) => accepted.row === duplicate.row)
      );
      setUploadDuplicates(remainingDuplicates);
      setSelectedDuplicates([]);

      // Refresh the leads list after creating duplicates
      setPage(1);
      setAllLeads([]);
      setHasMore(true);
      setIsLoadingMore(false);

      const freshData = await getLeadsByStatusAndProject(
        projectID,
        status,
        effectivePstatus,
        1,
        limit,
        searchQuery,
        sales,
        closureDateFilter,
        lastFollowUpFilter,
        nextFollowUpFilter,
        createdOnFilter,
        sourceFilter,
        qualificationTagsFilter,
        createdFromFilter,
        createdToFilter,
        nextFollowUpFromFilter,
        nextFollowUpToFilter
      );

      if (freshData?.data) {
        setAllLeads(freshData.data);
        const totalLeads = freshData?.meta?.total || 0;
        const isLastPage = freshData.data.length < limit;
        const hasMoreData = !isLastPage && freshData.data.length < totalLeads;
        setHasMore(hasMoreData);
      }

      // If no more duplicates and errors, close modal
      if (remainingDuplicates.length === 0 && uploadErrors.length === 0) {
        setIsUploadErrorModalOpen(false);
        setUploadErrors([]);
        setUploadSuccesses([]);
        setUploadDuplicates([]);
        setSelectedDuplicates([]);
        setSelectedFile(null); // Clear selected file
      }
    },
    onError: (error, variables) => {
      setIsProcessingDuplicates(false);
      mixpanel.track('Create Duplicate Leads Failed', {
        user_id: userId,
        error_message:
          error?.response?.data?.message || error?.message || 'Failed to create duplicate leads',
        duplicates_count: variables.duplicates.length,
        timestamp: new Date().toISOString()
      });
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to create duplicate leads'
      );
    }
  });

  const { mutate: handleDownloadLeadTemplate } = useMutation({
    mutationFn: async ({ projectId, salesId, templateType }) => {
      return await downloadLeadTemplate(projectId, salesId, templateType);
    },
    onSuccess: (data, variables) => {
      const { projectId, templateType } = variables;
      if (!data || data.size === 0) {
        toast.error('Error: File data is empty or invalid.');
        return;
      }

      // Get project name from AllProjects
      const selectedProject = projects?.find((project) => project.id === parseInt(projectId));
      const projectName = selectedProject?.name || `Project_${projectId}`;

      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Lead_${templateType === 'linkedin' ? 'LinkedIn' : 'Standard'}_Template_${projectName.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${templateType === 'linkedin' ? 'LinkedIn' : 'Standard'} Lead Template Downloaded`);
      setIsDownloadTemplateModalOpen(false);
      setDownloadProjectId('');
      setDownloadSalesId('');
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Error in downloading Lead Template'
      );
    }
  });

  // Handler for download template button
  const handleDownloadTemplateClick = () => {
    setIsDownloadTemplateModalOpen(true);
  };

  // Handler for upload template button
  const handleUploadTemplateClick = () => {
    setIsUploadTemplateModalOpen(true);
  };

  // Handler for file selection in upload modal
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Handler for upload template submission
  const handleUploadTemplateSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await uploadLeadTemplate(formData);
      const { errors, results, duplicates } = normalizeTemplateUploadResponse(response);

      // LinkedIn-only: open results for duplicates too. Standard lead uploads keep
      // the previous gate (errors only) so that flow is unchanged.
      const isLinkedInUpload =
        /linkedin/i.test(selectedFile?.name || '') ||
        [...results, ...duplicates].some((item) => {
          const data = item?.data ?? item;
          return (
            String(data?.source ?? '').toLowerCase() === 'linkedin' ||
            Boolean(data?.first_name || data?.linkedin_profile_url)
          );
        });

      if (errors.length > 0 || (isLinkedInUpload && duplicates.length > 0)) {
        setUploadErrors(errors);
        setUploadSuccesses(results);
        setUploadDuplicates(duplicates);
        setSelectedDuplicates([]);
        setIsUploadErrorModalOpen(true);
        setIsUploadTemplateModalOpen(false);
        setSelectedFile(null);
        if (isLinkedInUpload && errors.length > 0 && results.length === 0) {
          toast.error('No leads were uploaded. Fix the sheet errors and try again.');
        }
      } else {
        toast.success('Lead Template uploaded successfully');

        setPage(1);
        setAllLeads([]);
        setHasMore(true);
        setIsLoadingMore(false);

        // Force a fresh query with page 1
        const freshData = await getLeadsByStatusAndProject(
          projectID,
          status,
          effectivePstatus,
          1, // Force page 1
          limit,
          searchQuery,
          sales,
          closureDateFilter,
          lastFollowUpFilter,
          nextFollowUpFilter,
          createdOnFilter,
          sourceFilter,
          qualificationTagsFilter,
          createdFromFilter,
          createdToFilter,
          nextFollowUpFromFilter,
          nextFollowUpToFilter
        );

        if (freshData?.data) {
          setAllLeads(freshData.data);
          const totalLeads = freshData?.meta?.total || 0;
          const isLastPage = freshData.data.length < limit;
          const hasMoreData = !isLastPage && freshData.data.length < totalLeads;
          setHasMore(hasMoreData);
        }
        setIsUploadTemplateModalOpen(false);
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to upload Lead Template'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for project selection in download modal
  const handleDownloadProjectChange = (projectId) => {
    setDownloadProjectId(projectId);
    setDownloadSalesId(''); // Reset sales user when project changes
  };

  // Handler for download template submission
  const handleDownloadTemplateSubmit = (templateType = 'standard') => {
    if (!downloadProjectId) {
      toast.error('Please select a project');
      return;
    }
    if (!downloadSalesId) {
      toast.error('Please select a sales user');
      return;
    }
    handleDownloadLeadTemplate({
      projectId: downloadProjectId,
      salesId: downloadSalesId,
      templateType
    });
  };

  // Handler for accepting duplicates (used by reusable component)
  const handleAcceptDuplicates = (payload) => {
    createDuplicateLeadsMutation(payload);
  };

  // Handler for rejecting duplicates (used by reusable component)
  const handleRejectDuplicates = (duplicatesToReject) => {
    // Remove rejected duplicates from the list
    setUploadDuplicates((prev) =>
      prev.filter((item) => !duplicatesToReject.some((rejected) => rejected.row === item.row))
    );

    // If no more duplicates, close modal
    if (uploadDuplicates.length === duplicatesToReject.length && uploadErrors.length === 0) {
      setIsUploadErrorModalOpen(false);
      setUploadErrors([]);
      setUploadSuccesses([]);
      setUploadDuplicates([]);
      setSelectedDuplicates([]);
    }
  };

  // Handler for retry upload
  const handleRetryUpload = () => {
    setIsUploadErrorModalOpen(false);
    setUploadErrors([]);
    setUploadSuccesses([]);
    setUploadDuplicates([]);
    setSelectedDuplicates([]);
    // Reopen upload modal for retry
    setIsUploadTemplateModalOpen(true);
  };

  // Handler for downloading all leads as Excel
  // Handler for downloading all leads as CSV
  // Handler for downloading all leads as Excel using exceljs
  const handleDownloadExcel = async () => {
    if (isDownloadingExcel) return; // Prevent multiple clicks

    try {
      setIsDownloadingExcel(true);
      toast.loading('Preparing Excel file...', { id: 'excel-download' });

      // Track download event
      mixpanel.track('Excel Download Started', {
        user_id: userId,
        project_id: projectID,
        status: getStatusParam(pstatus) || 'all',
        timestamp: new Date().toISOString()
      });

      // Fetch all leads data (without pagination limit)
      const allLeadsData = await getLeadsByStatusAndProject(
        projectID,
        status,
        effectivePstatus,
        1, // page
        999999, // large limit to get all data
        searchQuery,
        sales,
        closureDateFilter,
        lastFollowUpFilter,
        nextFollowUpFilter,
        createdOnFilter,
        sourceFilter,
        qualificationTagsFilter,
        createdFromFilter,
        createdToFilter,
        nextFollowUpFromFilter,
        nextFollowUpToFilter
      );

      console.log('allLeadsData--', allLeadsData);

      if (!allLeadsData?.data || allLeadsData.data.length === 0) {
        toast.error('No data available to download', { id: 'excel-download' });
        setIsDownloadingExcel(false);
        return;
      }

      console.log('Excel Export - Total leads fetched:', allLeadsData.data.length);

      // Helper function to format dates
      const formatDateForExcel = (dateString) => {
        if (!dateString) return '---';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '---';
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-GB', options);
      };

      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sales Leads');

      // Define columns
      worksheet.columns = [
        { header: 'S.No', key: 's_no', width: 8 },
        { header: 'Lead Name', key: 'name', width: 30 },
        { header: 'Project', key: 'project', width: 20 },
        { header: 'Expected Budget Range (in Lacs)', key: 'budget', width: 25 },
        { header: 'Expected Closure Date', key: 'closure_date', width: 20 },
        { header: 'Lead Source', key: 'source', width: 20 },
        { header: 'Next Follow Up', key: 'next_follow_up', width: 20 },
        { header: 'Next Follow Up Agenda', key: 'next_follow_up_agenda', width: 28 },
        { header: 'Created On', key: 'created_on', width: 20 },
        { header: 'Assigned To', key: 'assigned_to', width: 20 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Phone', key: 'phone', width: 15 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'City', key: 'city', width: 20 },
        { header: 'State', key: 'state', width: 20 },
        { header: 'Alternate Phone', key: 'alternate_phone', width: 15 },
        { header: 'Occupation', key: 'occupation', width: 20 },
        { header: 'Reference', key: 'reference', width: 25 },
        { header: 'Remark', key: 'remark', width: 50 }
      ];

      // Add rows
      allLeadsData.data.forEach((el, index) => {
        worksheet.addRow({
          s_no: index + 1,
          name:
            `${el?.user?.name || ''}${el?.reference_name ? ` (${el?.reference_name})` : ''}` ||
            '---',
          project: el?.projects?.[0]?.name || '---',
          budget: el?.expectedBudgetRange || '---',
          closure_date: formatDateForExcel(el?.expected_clouser_date) || '---',
          source: getLeadSourceDisplay(el),
          next_follow_up: formatDateForExcel(getNextFollowUpForLead(el?.follow_ups).date) || '---',
          next_follow_up_agenda: getNextFollowUpForLead(el?.follow_ups).agenda || '---',
          created_on: formatDateForExcel(el?.created_at || el?.user?.created_at) || '---',
          assigned_to:
            el?.pstatus === 'Closed Lost' ? '' : el?.sales?.name || '---',
          status: el?.pstatus || '---',
          phone: el?.user?.phone_number || '---',
          email: el?.user?.email || '---',
          city: el?.address?.city || '---',
          state: el?.address?.state || '---',
          alternate_phone: el?.alternate_phone_number || '---',
          occupation: el?.occupation || '---',
          reference: el?.reference_name || '---',
          remark: getLeadRemark(el) || '---'
        });
      });

      // Style header row (bold)
      worksheet.getRow(1).font = { bold: true };

      // Generate buffer and save
      const buffer = await workbook.xlsx.writeBuffer();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `Sales_Leads_${timestamp}.xlsx`);

      toast.success(`Downloaded ${allLeadsData.data.length} leads successfully!`, {
        id: 'excel-download'
      });

      // Track successful download
      mixpanel.track('Excel Download Success', {
        user_id: userId,
        project_id: projectID,
        status: getStatusParam(pstatus) || 'all',
        leads_count: allLeadsData.data.length,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to download Excel file',
        { id: 'excel-download' }
      );

      // Track failed download
      mixpanel.track('Excel Download Failed', {
        user_id: userId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  // Dots animation effect for download button
  useEffect(() => {
    let interval;
    if (isDownloadingExcel) {
      interval = setInterval(() => {
        setDotCount((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setDotCount(0);
    }
    return () => clearInterval(interval);
  }, [isDownloadingExcel]);

  /*  
  
      JSX
    */
  return (
    <div className=" flex flex-col bg-gray-100 pb-10 overflow-hidden">
      <Loader isVisible={isSubmitting || (isFetching && page === 1 && allLeads.length === 0)} />
      {isAddLeadActionModalOpen && (
        <Modal
          width={46}
          confirmOnClose={true}
          isDirty={true}
          name="Add Lead"
          onClose={() => {
            setIsAddLeadActionModalOpen(false);
          }}>
          <div className="flex flex-col gap-5 w-full">
            <p className="text-sm text-gray-500 px-1">
              Choose your preferred method to bring new leads into the system.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-[#2A565E]/40">
                <div className="flex h-full flex-col items-center text-center gap-3">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#2A565E]/10 text-[#2A565E]">
                    <Users size={24} />
                  </span>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800">Single Lead</p>
                    <p className="mt-1 text-sm text-gray-500">Create manually in a guided form</p>
                  </div>
                  <button
                    type="button"
                    className="mt-1 inline-flex items-center justify-center rounded-lg bg-[#2A565E] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#21474d]"
                    onClick={() => {
                      setIsAddLeadActionModalOpen(false);
                      setIsCreateLeadModalOpen(true);
                    }}>
                    Create Lead
                  </button>
                </div>
              </div>

              <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-[#2A565E]/40">
                <div className="flex h-full flex-col items-center text-center gap-3">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#2A565E]/10 text-[#2A565E]">
                    <Upload size={24} />
                  </span>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800">Bulk Import</p>
                    <p className="mt-1 text-sm text-gray-500">Upload CSV/Excel for multiple leads</p>
                  </div>
                  <button
                    type="button"
                    className="mt-1 inline-flex items-center justify-center rounded-lg bg-[#2A565E] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#21474d]"
                    onClick={() => {
                      setIsAddLeadActionModalOpen(false);
                      setIsUploadTemplateModalOpen(true);
                    }}>
                    Upload File
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                    onClick={() => {
                      setIsAddLeadActionModalOpen(false);
                      handleDownloadTemplateClick();
                    }}>
                    <Download size={16} />
                    <span>Download Template</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
      {isCreateLeadModalOpen && (
        <Modal
          width={50}
          confirmOnClose={true}
          isDirty={true}
          name="Add New Lead"
          onClose={() => {
            setIsCreateLeadModalOpen(false);
          }}>
          <AddLeadForm
            onSubmit={onSubmitCreate}
            onCancel={() => setIsCreateLeadModalOpen(false)}
            isSubmitting={isSubmitting}
            AllProjects={projects}
          />
        </Modal>
      )}

      {/* Edit Lead Modal */}
      {isEditLeadModalOpen && (
        <Modal
          width={50}
          confirmOnClose={true}
          isDirty={true}
          name="Edit Lead"
          onClose={() => {
            setIsEditLeadModalOpen(false);
            setEditingLead(null);
          }}>
          <EditLeadForm
            onSubmit={onSubmitEdit}
            onCancel={() => {
              setIsEditLeadModalOpen(false);
              setEditingLead(null);
            }}
            isSubmitting={isSubmitting}
            AllProjects={projects}
            editingLead={editingLead}
          />
        </Modal>
      )}

      {/* Duplicate Lead Modal */}
      {isCloneLeadModalOpen && (
        <Modal
          width={40}
          confirmOnClose={true}
          isDirty={true}
          name={`Duplicate Lead '${cloningLead?.user?.name}'`}
          onClose={() => {
            setIsCloneLeadModalOpen(false);
            setCloningLead(null);
            setSelectedCloneProjects([]);
          }}>
          <div className="flex flex-col justify-between items-start gap-4">
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full">
                <label className="text-gray-600 mb-2 block">Select Projects</label>
                <Multiselect
                  key={`multiselect-${cloningLead?.id}`}
                  options={
                    projects
                      ?.filter((project) => {
                        const currentProjectId = cloningLead?.interested_projects?.[0];
                        return currentProjectId ? project.id !== currentProjectId : true;
                      })
                      ?.map((project) => ({
                        id: project.id.toString(),
                        name: project.name
                      })) || []
                  }
                  disable={!projects?.length}
                  selectedValues={selectedCloneProjects}
                  onSelect={(selectedList) => {
                    setSelectedCloneProjects(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    setSelectedCloneProjects(selectedList);
                  }}
                  displayValue="name"
                  placeholder="Select projects"
                  emptyRecordMsg="No projects available"
                  showCheckbox
                  style={{
                    optionContainer: { border: '1px solid', backgroundColor: 'white' },
                    option: { color: 'black', backgroundColor: 'white' },
                    chips: { background: '#2A565E', color: 'white' }
                  }}
                />
              </div>
              <div className="flex justify-end items-center space-x-4 mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setIsCloneLeadModalOpen(false);
                    setCloningLead(null);
                    setSelectedCloneProjects([]);
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={onSubmitClone}
                  disabled={isSubmitting || selectedCloneProjects.length === 0}>
                  Duplicate Lead
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {openAddTargetModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Add Target"
          onClose={handleToggleAddUser}>
          <AddSalesTarget onSubmit={''} onClose={handleToggleAddUser} />
        </Modal>
      )}
      {isUpdateModalOpen && (
        <Modal
          width={40}
          name="Update Sales User"
          onClose={() => setIsUpdateModalOpen(false)}
          confirmOnClose={true}
          isDirty={true}>
          <UpdateSalesUser
            leadId={selectedLead?.id}
            currentSalesId={selectedLead?.salesId}
            projectId={selectedLead?.projectId || projectID}
            onClose={() => setIsUpdateModalOpen(false)}
            onSuccess={async () => {
              setPage(1);
              setAllLeads([]);
              setHasMore(true);
              setIsLoadingMore(false);

              // Force a fresh query with page 1
              const freshData = await getLeadsByStatusAndProject(
                projectID,
                status,
                effectivePstatus,
                1, // Force page 1
                limit,
                searchQuery,
                sales,
                closureDateFilter,
                lastFollowUpFilter,
                nextFollowUpFilter,
                createdOnFilter,
                sourceFilter,
                qualificationTagsFilter,
                createdFromFilter,
                createdToFilter,
                nextFollowUpFromFilter,
                nextFollowUpToFilter
              );

              if (freshData?.data) {
                setAllLeads(freshData.data);
                const totalLeads = freshData?.meta?.total || 0;
                const isLastPage = freshData.data.length < limit;
                const hasMoreData = !isLastPage && freshData.data.length < totalLeads;
                setHasMore(hasMoreData);
              }
              setIsUploadTemplateModalOpen(false);
              setUploadProjectId('');
              setUploadSalesId('');
              setSelectedFile(null);
              setIsUpdateModalOpen(false);
            }}
          />
        </Modal>
      )}

      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this lead'}'`}
          onClose={() => {
            setCanDelete(null);
            setIsBulk(false);
            setSelectAll(false);
            setSelectedItems([]);
            setSelectedIds([]);
          }}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this lead has dependencies, please delete following stages to proceed
            </p>
            <div className="flex justify-start, align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {resData &&
                resData.reason?.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < resData.reason.length - 1 ? ', ' : ''}
                  </span>
                ))}
            </div>
          </div>
          <div className="flex flex-col justify-start items-center mt-4 w-[100%]  bg-[#f7f4d0] pt-[4px] pb-[4px] pl-2 pr-2 rounded-[4px]">
            <div className="flex justify-between w-[99%] items-center bg-white p-2 pt-1 pb-1">
              <p>Would you like to delete more leads ?</p>
              <div
                onClick={(e) => {
                  setIsBulk(!isBulk);
                  setSelectedItems([]);
                  setSelectAll(false);
                }}
                className="bg-amber-100 p-2 pt-1 pb-1 rounded-[5px] flex justify-center items-center gap-2 cursor-pointer">
                Bulk Delete
                <input
                  type="checkbox"
                  checked={isBulk}
                  readOnly
                  className="outline-none border-none"
                />
              </div>
            </div>

            {isBulk && (
              <div className="flex justify-end w-[100%] pr-[1.2rem] pt-3">
                <div className="flex gap-2">
                  <p>Select All: </p>
                  <input
                    className="outline-none border-none"
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </div>
              </div>
            )}
            {isBulk && (
              <div className="pl-[10px] w-[100%] pr-[19px] mt-2 mb-2 max-h-[14rem] overflow-auto">
                {leadsData &&
                  leadsData?.map((c, index) => (
                    <div
                      key={`${c.id}-${index}`}
                      className="flex justify-between items-center text-[#5b5b5b]">
                      {index + 1}. {c.name}
                      <input
                        className="outline-none border-none"
                        type="checkbox"
                        checked={selectedItems?.includes(c.id)}
                        disabled={selectedIds?.includes(c.id)} // Disable if already selected
                        onChange={(e) => HandleFunctionCalls(c.id, e.target.checked)}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="reset"
              onClick={() => {
                setCanDelete(null);
                setIsBulk(false);
                setSelectAll(false);
                setSelectedItems([]);
                setSelectedIds([]);
              }}
              displayType="secondary">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                HandleDelete(selectedItems);
              }}
              disabled={isSubmitting || selectedItems.length === 0}
              displayType="primary-red">
              Delete
            </Button>
          </div>
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'lead'}
            onClose={() => setCanDelete(null)}
            data={leadsData}
            type={'lead'}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      {/* Download Template Modal */}
      {isDownloadTemplateModalOpen && (
        <Modal
          width={40}
          confirmOnClose={true}
          isDirty={true}
          name="Download Lead Template"
          onClose={() => {
            setIsDownloadTemplateModalOpen(false);
            setDownloadProjectId('');
            setDownloadSalesId('');
          }}>
          <div className="flex flex-col justify-between items-start gap-4">
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full">
                <label className="text-gray-600 mb-2 block">Select Project</label>
                <select
                  value={downloadProjectId}
                  onChange={(e) => handleDownloadProjectChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Select a project</option>
                  {projects?.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-full">
                <label className="text-gray-600 mb-2 block">Select Sales User</label>
                <select
                  value={downloadSalesId}
                  onChange={(e) => setDownloadSalesId(e.target.value)}
                  disabled={!downloadProjectId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="">Select a sales user</option>
                  {formattedSalesUsers?.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end items-center space-x-4 mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setIsDownloadTemplateModalOpen(false);
                    setDownloadProjectId('');
                    setDownloadSalesId('');
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => handleDownloadTemplateSubmit('standard')}
                  disabled={!downloadProjectId || !downloadSalesId || isSubmitting}>
                  {isSubmitting ? 'Downloading...' : 'Standard Template'}
                </Button>
                <Button
                  type="button"
                  onClick={() => handleDownloadTemplateSubmit('linkedin')}
                  disabled={!downloadProjectId || !downloadSalesId || isSubmitting}>
                  {isSubmitting ? 'Downloading...' : 'LinkedIn Template'}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Upload Template Modal */}
      {isUploadTemplateModalOpen && (
        <Modal
          width={50}
          confirmOnClose={true}
          isDirty={true}
          name="Upload Lead Template"
          onClose={() => {
            setIsUploadTemplateModalOpen(false);
            setSelectedFile(null);
          }}>
          <div className="flex flex-col justify-between items-start gap-4">
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full">
                <label className="text-gray-600 mb-2 block">Select File</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-1">Selected: {selectedFile.name}</p>
                )}
              </div>

              <div className="flex justify-end items-center space-x-4 mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setIsUploadTemplateModalOpen(false);
                    setSelectedFile(null);
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleUploadTemplateSubmit}
                  disabled={!selectedFile || isSubmitting}>
                  {isSubmitting ? 'Uploading...' : 'Upload Template'}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Upload Result Modal */}
      <UploadResultsModal
        isOpen={isUploadErrorModalOpen}
        onClose={() => {
          setIsUploadErrorModalOpen(false);
          setUploadErrors([]);
          setUploadSuccesses([]);
          setUploadDuplicates([]);
          setSelectedDuplicates([]);
        }}
        uploadSuccesses={uploadSuccesses}
        uploadErrors={uploadErrors}
        uploadDuplicates={uploadDuplicates}
        onAcceptDuplicates={handleAcceptDuplicates}
        onRejectDuplicates={handleRejectDuplicates}
        isProcessingDuplicates={isProcessingDuplicates}
        onRetryUpload={handleRetryUpload}
      />
      {showToolbar && (
      <Headroom
        disable
        style={{
          position: 'fixed',
          top: '4.6rem',
          left: collapsed ? '76px' : '16.6%',
          right: '0',
          zIndex: 10,
          transition: 'all 0.3s ease-in-out'
        }}>
        <div className="flex flex-col gap-2 bg-white border-t-[2px] border-stone-100 px-3 py-3 shadow-sm transition-all duration-300 ease-in-out sm:px-6 sm:py-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:gap-3">
            {variant === 'list' && (
              <button
                type="button"
                onClick={() => navigate('/builder/sales/', { state: { secondaryTab: 'Leads' } })}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-[#2A565E] shadow-sm transition-colors hover:bg-gray-50"
                aria-label="Back to leads overview">
                <ChevronLeft size={22} strokeWidth={2} />
              </button>
            )}
            <Search
              onChange={handleSearchChange}
              query={searchQuery}
              onClick={handleOnClickSearch}
              placeholder="Search Leads ..."
              className="min-w-[12rem] flex-1 basis-[12rem] sm:max-w-[18rem] lg:max-w-[24rem]"
            />
            <div className="relative shrink-0" ref={projectHeaderRef}>
              <button
                type="button"
                data-dropdown-trigger
                onClick={(e) => {
                  e.stopPropagation();
                  handleHeaderDropdownClick('project', e);
                }}
                className="inline-flex h-10 max-w-[11rem] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 sm:max-w-[14rem]">
                <span className="truncate">
                  {!projectID ? 'All Projects' : activeFilters.project || 'Project Filter'}
                </span>
                <ChevronDown size={16} className="shrink-0" />
              </button>
              {openDropdown === 'project' && (
                <div
                  className="absolute mt-1 bg-white border rounded shadow z-[1000] left-0 min-w-[14rem] max-h-[250px] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}>
                  {[
                    { id: '__all_projects__', label: 'All Projects' },
                    ...projectsForDropdown
                  ].map((proj, idx) => {
                    const isAllRow = proj.id === '__all_projects__';
                    const isSelected = isAllRow
                      ? !projectID
                      : String(proj.id) === String(projectID);
                    return (
                    <div
                      key={isAllRow ? 'all-projects' : proj.id ?? idx}
                      className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                        isSelected ? 'bg-blue-100' : ''
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectDropdownItem('project', isAllRow ? '' : proj.id);
                      }}>
                      {proj.label}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="relative shrink-0" ref={sourceHeaderRef}>
              <button
                type="button"
                data-dropdown-trigger
                onClick={(e) => {
                  e.stopPropagation();
                  handleHeaderDropdownClick('source', e);
                }}
                className="inline-flex h-10 max-w-[11rem] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 sm:max-w-[14rem]">
                <span className="truncate">
                  {sourceFilter ? getLeadSourceFilterLabel(sourceFilter) : 'Lead Source'}
                </span>
                <ChevronDown size={16} className="shrink-0" />
              </button>
              {openDropdown === 'source' && (
                <div
                  className="absolute mt-1 bg-white border rounded shadow z-[1000] left-0 min-w-[16rem] max-h-[280px] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}>
                  {[
                    {
                      id: '',
                      value: '',
                      label: sourceFilter ? 'Clear Filter' : 'Select Source'
                    },
                    ...leadSourceOptions
                  ].map((option, idx) => (
                    <div
                      key={option.id || idx}
                      className={`px-4 py-2 hover:bg-gray-100 cursor-pointer whitespace-nowrap ${
                        option.id === '' ? 'bg-gray-100' : ''
                      } ${option.value === sourceFilter ? 'bg-blue-100' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectDropdownItem('source', option.value);
                      }}>
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative shrink-0">
              <button
                type="button"
                data-dropdown-trigger
                onClick={(e) => {
                  e.stopPropagation();
                  handleHeaderDropdownClick('qualificationTags', e);
                }}
                className="inline-flex h-10 max-w-[12rem] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 sm:max-w-[14rem]">
                <span className="truncate">
                  {qualificationTagsFilter.length
                    ? `Tags (${qualificationTagsFilter.length})`
                    : 'Qualification tags'}
                </span>
                <ChevronDown size={16} className="shrink-0" />
              </button>
              {openDropdown === 'qualificationTags' && (
                <div
                  className="absolute mt-1 bg-white border rounded shadow z-[1000] left-0 min-w-[16rem] max-h-[280px] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}>
                  <div
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer bg-gray-100 text-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectDropdownItem('qualificationTag', '');
                    }}>
                    {qualificationTagsFilter.length ? 'Clear tags' : 'Select tags'}
                  </div>
                  {qualificationTagOptions.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500">No tag options yet</div>
                  ) : (
                    qualificationTagOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer whitespace-nowrap text-sm ${
                          qualificationTagsFilter.includes(option.value) ? 'bg-blue-100' : ''
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelectDropdownItem('qualificationTag', option.value);
                        }}>
                        {option.label}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
            {canManageQualQuestions && (
              <button
                type="button"
                onClick={() => setShowQualQuestionsModal(true)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-[#2A565E] px-3 py-2 text-sm font-semibold text-[#2A565E] hover:bg-[#2A565E]/5"
                title="Manage qualification questions">
                <span className="hidden xl:inline">Qualification questions</span>
                <span className="xl:hidden">Qual. Qs</span>
              </button>
            )}
            {canBulkTransferLeads && (
              <button
                type="button"
                onClick={() => {
                  if (transferBulkMode) {
                    exitTransferBulkMode();
                  } else {
                    if (waBulkMode) exitWaBulkMode();
                    if (deleteBulkMode) exitDeleteBulkMode();
                    setTransferBulkMode(true);
                  }
                }}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  transferBulkMode
                    ? 'bg-[#2A565E] text-white border-[#2A565E]'
                    : 'bg-white text-[#2A565E] border-[#2A565E] hover:bg-[#2A565E]/5'
                }`}
                title="Select leads to transfer in bulk">
                <Users size={16} className="shrink-0" />
                <span className="hidden lg:inline">
                  {transferBulkMode ? 'Cancel transfer' : 'Bulk transfer'}
                </span>
              </button>
            )}
            {canDeleteLeads && (
              <button
                type="button"
                onClick={() => {
                  if (deleteBulkMode) {
                    exitDeleteBulkMode();
                  } else {
                    if (waBulkMode) exitWaBulkMode();
                    if (transferBulkMode) exitTransferBulkMode();
                    setDeleteBulkMode(true);
                  }
                }}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  deleteBulkMode
                    ? 'bg-rose-600 text-white border-rose-600'
                    : 'bg-white text-rose-600 border-rose-600 hover:bg-rose-50'
                }`}
                title="Select leads to delete in bulk">
                <Trash2 size={16} className="shrink-0" />
                <span className="hidden lg:inline">
                  {deleteBulkMode ? 'Cancel delete' : 'Bulk delete'}
                </span>
              </button>
            )}
            {projectID && (
              <button
                type="button"
                onClick={() => {
                  if (waBulkMode) {
                    exitWaBulkMode();
                  } else {
                    if (transferBulkMode) exitTransferBulkMode();
                    if (deleteBulkMode) exitDeleteBulkMode();
                    setWaBulkMode(true);
                  }
                }}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  waBulkMode
                    ? 'bg-[#2A565E] text-white border-[#2A565E]'
                    : 'bg-white text-[#2A565E] border-[#2A565E] hover:bg-[#2A565E]/5'
                }`}
                title="Select leads to send WhatsApp">
                <MessageCircle size={16} className="shrink-0" />
                <span className="hidden md:inline">
                  {waBulkMode ? 'Cancel selection' : 'Send WhatsApp'}
                </span>
              </button>
            )}
            <button
              type="button"
              className={`relative flex items-center justify-center ${
                isDownloadingExcel || isSubmitting
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }`}
              onClick={handleDownloadExcel}
              disabled={isDownloadingExcel || isSubmitting || !leadsData || leadsData.length === 0}
              aria-label="Download Excel"
              title="Download all leads as Excel">
              <img height={35} width={35} src={FileDownload} alt="Download Excel" />
              {isDownloadingExcel && (
                <span className="absolute top-10 left-0 text-sm text-gray-700 whitespace-nowrap">
                  Downloading{'.'.repeat(dotCount)}
                </span>
              )}
            </button>
          </div>
          </div>

          {(activeFilters.sales ||
            sourceFilter ||
            qualificationTagsFilter.length > 0 ||
            closureDateFilter ||
            lastFollowUpFilter ||
            nextFollowUpFilter ||
            createdOnFilter ||
            (!isLeadTemperatureLockedRoute && pstatus.length > 0)) && (
            <div className="flex min-w-0 flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
              <span className="shrink-0 text-xs font-medium text-gray-600">Filtering by:</span>
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {activeFilters.sales && (
                    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                      <span className="truncate">{activeFilters.sales}</span>
                      <button
                        onClick={() => clearFilter('sales')}
                        className="ml-0.5 shrink-0 text-sm font-bold text-green-600 hover:text-green-800">
                        x
                      </button>
                    </span>
                  )}
                  {!isLeadTemperatureLockedRoute &&
                    pstatus.map((status) => (
                      <span
                        key={status}
                        className="inline-flex max-w-full items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-800">
                        <span className="truncate">{status}</span>
                        <button
                          onClick={() => removeStatusFilter(status)}
                          className="ml-0.5 shrink-0 text-sm font-bold text-purple-600 hover:text-purple-800">
                          x
                        </button>
                      </span>
                    ))}
                  {sourceFilter && (
                    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-xs text-teal-800">
                      <span className="truncate">
                        {getLeadSourceFilterLabel(sourceFilter) || sourceFilter}
                      </span>
                      <button
                        onClick={() => clearFilter('source')}
                        className="ml-0.5 shrink-0 text-sm font-bold text-teal-600 hover:text-teal-800">
                        x
                      </button>
                    </span>
                  )}
                  {qualificationTagsFilter.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex max-w-full items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
                      <span className="truncate">{tagLabelByValue[tag] || tag}</span>
                      <button
                        onClick={() => handleSelectDropdownItem('qualificationTag', tag)}
                        className="ml-0.5 shrink-0 text-sm font-bold text-amber-700 hover:text-amber-900">
                        x
                      </button>
                    </span>
                  ))}
                  {closureDateFilter && (
                    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-800">
                      <span className="truncate">
                        {closureDateOptions.find((opt) => opt.value === closureDateFilter)?.label ||
                          closureDateFilter}
                      </span>
                      <button
                        onClick={() => clearFilter('closureDate')}
                        className="ml-0.5 shrink-0 text-sm font-bold text-orange-600 hover:text-orange-800">
                        x
                      </button>
                    </span>
                  )}
                  {lastFollowUpFilter && (
                    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-cyan-100 px-2 py-0.5 text-xs text-cyan-800">
                      <span className="truncate">
                        {lastFollowUpOptions.find((opt) => opt.value === lastFollowUpFilter)
                          ?.label || lastFollowUpFilter}
                      </span>
                      <button
                        onClick={() => clearFilter('lastFollowUp')}
                        className="ml-0.5 shrink-0 text-sm font-bold text-cyan-600 hover:text-cyan-800">
                        x
                      </button>
                    </span>
                  )}
                  {nextFollowUpFilter && (
                    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800">
                      <span className="truncate">
                        {nextFollowUpFilter === 'custom' &&
                        nextFollowUpFromFilter &&
                        nextFollowUpToFilter
                          ? `${nextFollowUpFromFilter} → ${nextFollowUpToFilter}`
                          : nextFollowUpOptions.find((opt) => opt.value === nextFollowUpFilter)
                              ?.label || nextFollowUpFilter}
                      </span>
                      <button
                        onClick={() => clearFilter('nextFollowUp')}
                        className="ml-0.5 shrink-0 text-sm font-bold text-indigo-600 hover:text-indigo-800">
                        x
                      </button>
                    </span>
                  )}
                  {createdOnFilter && (
                    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-pink-100 px-2 py-0.5 text-xs text-pink-800">
                      <span className="truncate">
                        {createdOnFilter === 'custom' && createdFromFilter && createdToFilter
                          ? `${createdFromFilter} → ${createdToFilter}`
                          : createdOnOptions.find((opt) => opt.value === createdOnFilter)?.label ||
                            createdOnFilter}
                      </span>
                      <button
                        onClick={() => clearFilter('createdOn')}
                        className="ml-0.5 shrink-0 text-sm font-bold text-pink-600 hover:text-pink-800">
                        x
                      </button>
                    </span>
                  )}
              </div>
            </div>
          )}
        </div>
      </Headroom>
      )}
      {showMetrics && (
      <div
        className={`w-full px-3 sm:px-6 mb-4 ${variant === 'metrics' ? 'mt-[7.5rem]' : 'mt-[7.5rem]'}`}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {metricCards.map((metric) => (
            <div
              key={metric.title}
              onClick={metric.onClick}
              className={metric.onClick ? 'cursor-pointer' : ''}>
              {metric.variant === 'addLead' ? (
                <div className="h-[92px] rounded-xl border border-dashed border-[#9DB5BE] bg-[#F5F7F8] px-4 flex items-center justify-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#DDE4E7] text-[#2A565E]">
                    <Plus size={20} />
                  </span>
                  <span className="text-base font-semibold text-[#2A565E]">Add Lead</span>
                </div>
              ) : (
                <MetricCard
                  title={metric.title}
                  value={metric.value}
                  icon={metric.icon}
                  iconBgClass={metric.iconBgClass}
                  iconColorClass={metric.iconColorClass}
                  valueColorClass={metric.valueColorClass}
                  borderClass={metric.borderClass}
                  showArrow
                  titleClassName="text-xs"
                  valueClassName="text-2xl sm:text-3xl"
                  className="h-[92px]"
                />
              )}
            </div>
          ))}
          <LeadsAiBrief
            projectId={forcedProjectID ?? projectID}
            pipelineCounts={{
              hot: leadMetrics.hotLeads,
              warm: leadMetrics.warmLeads,
              cold: leadMetrics.coldLeads,
              demo: leadMetrics.demoScheduledLeads,
              nurture: leadMetrics.nurtureLeads,
              futureProspect: leadMetrics.futureProspectLeads
            }}
          />
          <LeadProgressionFlowCard
            projectId={forcedProjectID ?? projectID}
            salesUsers={salesVerticalUsers}
            isSalesLow={isSalesExecutive}
            currentUserId={userId}
          />
        </div>
      </div>
      )}
      {showTable && (
      <>
      <div
        className={`px-3 sm:px-6 pb-6 ${variant === 'list' ? 'mt-[7.5rem]' : ''}`}>
      {!showMetrics && !isClosedLostOnlyView && (
        <LeadsAiSummaryBanner
          projectId={forcedProjectID ?? projectID}
          pipelineCounts={{
            hot: leadMetrics.hotLeads,
            warm: leadMetrics.warmLeads,
            cold: leadMetrics.coldLeads,
            demo: leadMetrics.demoScheduledLeads,
            nurture: leadMetrics.nurtureLeads,
            futureProspect: leadMetrics.futureProspectLeads
          }}
          className="mb-6"
        />
      )}
      <GridWrapper width={100} childrenCustomStyle={variant === 'list' ? 'm-0' : undefined}>
        <div className="min-h-[25.6rem] relative mb-[3rem] rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col items-center gap-4 overflow-hidden">
          <div
            className="overflow-scroll hide-scrollbar w-full relative rounded-xl"
            id="scrollableDiv">
            <InfiniteScroll
              dataLength={allLeads.length}
              next={() => {
                console.log('Ã°Å¸â€â€ž InfiniteScroll next function called directly');
                loadMore();
              }}
              hasMore={effectiveHasMore}
              loader={
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading more leads...</span>
                </div>
              }
              endMessage={
                <div className="flex justify-center items-center py-4">
                  <p className="text-gray-500">No more leads to load</p>
                </div>
              }
              style={{ overflow: 'visible' }}
              onScroll={() => {
                console.log('Ã°Å¸â€œÅ“ InfiniteScroll onScroll triggered');
              }}>
              <table className="text-left text-sm w-full">
                <thead
                  className="bg-gray-100 !text-gray-600 w-full"
                  style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                  <tr className="h-12 w-full">
                    <th className="px-4 py-2 border-b border-gray-200 font-medium">Lead Name</th>
                    {/* <th className="px-4 py-2 border-b font-normal relative" ref={projectHeaderRef}>
                      <span>Project</span>
                      <button
                        className="ml-1 align-middle"
                        data-dropdown-trigger
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('project', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'project' && (
                        <div
                          className="absolute bg-white border rounded shadow z-[1000] top-full left-0 min-w-full"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            minWidth: '100%',
                            maxHeight: '250px',
                            overflowY: 'auto'
                          }}
                          onClick={(e) => {
                            console.log('Project dropdown clicked');
                            e.stopPropagation();
                          }}>
                          {[
                            {
                              id: '',
                              value: '',
                              label: projectID ? 'Clear Filter' : 'Select Project',
                              selected: true
                            },
                            ...projectsForDropdown
                          ].map((proj, idx) => (
                            <div
                              key={proj.id || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                proj.id === '' && 'bg-gray-100'
                              } ${proj.id === projectID && 'bg-blue-100'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Project item clicked:', proj);
                                handleSelectDropdownItem('project', proj.id);
                              }}>
                              {proj.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </th> */}
                    {/* <th
                      className="px-4 py-2 border-b font-normal relative"
                      ref={lastFollowUpHeaderRef}>
                      <span>Last Follow Up</span>
                      <button
                        className="ml-1 align-middle"
                        data-dropdown-trigger
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('lastFollowUp', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'lastFollowUp' && (
                        <div
                          className="absolute bg-white border rounded shadow z-[1000] top-full left-0 min-w-full"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            minWidth: '100%',
                            maxHeight: '250px',
                            overflowY: 'auto'
                          }}
                          onClick={(e) => {
                            console.log('Last follow up dropdown clicked');
                            e.stopPropagation();
                          }}>
                          {[
                            {
                              id: '',
                              value: '',
                              label: lastFollowUpFilter ? 'Clear Filter' : 'Select Date Range',
                              selected: true
                            },
                            ...lastFollowUpOptions
                          ].map((option, idx) => (
                            <div
                              key={option.id || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                option.id === '' && 'bg-gray-100'
                              } ${option.value === lastFollowUpFilter && 'bg-blue-100'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Last follow up item clicked:', option);
                                handleSelectDropdownItem('lastFollowUp', option.value);
                              }}>
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </th> */}
                    <th
                      className="px-4 py-2 border-b border-gray-200 font-medium relative"
                      ref={nextFollowUpHeaderRef}>
                      <span>Next Follow Up</span>
                      <button
                        className="ml-1 align-middle"
                        data-dropdown-trigger
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('nextFollowUp', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'nextFollowUp' && (
                        <PortalDropdown
                          anchorRect={
                            nextFollowUpDropdownRect ||
                            nextFollowUpHeaderRef.current?.getBoundingClientRect()
                          }
                          minWidth={240}>
                          {[
                            {
                              id: '',
                              value: '',
                              label: nextFollowUpFilter ? 'Clear Filter' : 'Select Date Range'
                            },
                            ...nextFollowUpOptions,
                            { id: 'custom', value: 'custom', label: 'Custom range' }
                          ].map((option, idx) => (
                            <div
                              key={option.id || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                option.id === '' && 'bg-gray-100'
                              } ${option.value === nextFollowUpFilter && 'bg-blue-100'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelectDropdownItem(
                                  'nextFollowUp',
                                  option.value,
                                  option.value === 'custom'
                                );
                              }}>
                              {option.label}
                            </div>
                          ))}
                          {(nextFollowUpFilter === 'custom' ||
                            openDropdown === 'nextFollowUp') && (
                            <div className="border-t px-3 py-3 space-y-2 bg-gray-50">
                              <p className="text-xs font-medium text-gray-600">Custom date range</p>
                              <label className="block text-[11px] text-gray-500">From</label>
                              <input
                                type="date"
                                value={nextFollowUpFromFilter || ''}
                                onChange={(e) =>
                                  setNextFollowUpFromFilter(e.target.value || null)
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                              <label className="block text-[11px] text-gray-500">To</label>
                              <input
                                type="date"
                                value={nextFollowUpToFilter || ''}
                                onChange={(e) => setNextFollowUpToFilter(e.target.value || null)}
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                              <button
                                type="button"
                                onClick={applyNextFollowUpCustomRange}
                                className="w-full mt-1 rounded bg-[#2A565E] text-white text-sm py-1.5 hover:opacity-90">
                                Apply range
                              </button>
                            </div>
                          )}
                        </PortalDropdown>
                      )}
                    </th>
                    <th className="px-4 py-2 border-b border-gray-200 font-medium">
                      Next Follow Up Agenda
                    </th>
                    <th
                      className="px-4 py-2 border-b border-gray-200 font-medium relative"
                      ref={createdOnHeaderRef}>
                      <span>Created On</span>
                      <button
                        className="ml-1 align-middle"
                        data-dropdown-trigger
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('createdOn', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'createdOn' && (
                        <PortalDropdown
                          anchorRect={
                            createdOnDropdownRect ||
                            createdOnHeaderRef.current?.getBoundingClientRect()
                          }
                          minWidth={240}>
                          {[
                            {
                              id: '',
                              value: '',
                              label: createdOnFilter ? 'Clear Filter' : 'Select Date Range'
                            },
                            ...createdOnOptions,
                            { id: 'custom', value: 'custom', label: 'Custom range' }
                          ].map((option, idx) => (
                            <div
                              key={option.id || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                option.id === '' && 'bg-gray-100'
                              } ${option.value === createdOnFilter && 'bg-blue-100'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelectDropdownItem(
                                  'createdOn',
                                  option.value,
                                  option.value === 'custom'
                                );
                              }}>
                              {option.label}
                            </div>
                          ))}
                          {(createdOnFilter === 'custom' || openDropdown === 'createdOn') && (
                            <div className="border-t px-3 py-3 space-y-2 bg-gray-50">
                              <p className="text-xs font-medium text-gray-600">Custom date range</p>
                              <label className="block text-[11px] text-gray-500">From</label>
                              <input
                                type="date"
                                value={createdFromFilter || ''}
                                onChange={(e) => setCreatedFromFilter(e.target.value || null)}
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                              <label className="block text-[11px] text-gray-500">To</label>
                              <input
                                type="date"
                                value={createdToFilter || ''}
                                onChange={(e) => setCreatedToFilter(e.target.value || null)}
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                              <button
                                type="button"
                                onClick={applyCreatedOnCustomRange}
                                className="w-full mt-1 rounded bg-[#2A565E] text-white text-sm py-1.5 hover:opacity-90">
                                Apply range
                              </button>
                            </div>
                          )}
                        </PortalDropdown>
                      )}
                    </th>
                    <th className="px-4 py-2 border-b border-gray-200 font-medium relative" ref={salesHeaderRef}>
                      <span>Assigned</span>
                      <button
                        className="ml-1 align-middle"
                        data-dropdown-trigger
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('assigned', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'assigned' && (
                        <div
                          className="absolute bg-white border rounded shadow z-[1000] top-full left-0 min-w-full"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            minWidth: '100%',
                            maxHeight: '250px',
                            overflowY: 'auto'
                          }}
                          onClick={(e) => {
                            console.log('Assigned dropdown clicked');
                            e.stopPropagation();
                          }}>
                          {[
                            {
                              id: '',
                              name: sales ? 'Clear Filter' : 'Select Sales',
                              selected: true
                            },
                            ...salesVerticalUsers
                          ].map((guy, idx) => (
                            <div
                              key={guy.id || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                guy.id === '' && 'bg-gray-100'
                              } ${guy.id === sales && 'bg-blue-100'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Assigned item clicked:', guy);
                                handleSelectDropdownItem('assigned', guy.id);
                              }}>
                              {guy.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </th>
                    <th
                      className="px-4 py-2 border-b border-gray-200 font-medium relative"
                      ref={isLeadTemperatureLockedRoute ? undefined : statusHeaderRef}>
                      <span>Status</span>
                      {!isLeadTemperatureLockedRoute && (
                        <>
                          <button
                            className="ml-1 align-middle"
                            data-dropdown-trigger
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHeaderDropdownClick('status', e);
                            }}>
                            <ChevronDown size={16} />
                          </button>
                          {openDropdown === 'status' && (
                            <div
                              className="absolute bg-white border rounded shadow z-[1000] top-full left-0 min-w-full"
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                minWidth: '100%',
                                maxHeight: '250px',
                                overflowY: 'auto'
                              }}
                              onClick={(e) => {
                                console.log('Status dropdown clicked');
                                e.stopPropagation();
                              }}>
                              <div
                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer bg-gray-100 ${
                                  pstatus.length === 0 ? 'bg-blue-100' : ''
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSelectDropdownItem('status', 'all', true);
                                }}>
                                {pstatus.length > 0 ? 'Clear Filter' : 'Select Status'}
                              </div>
                              {statusOptions.map((status, idx) => (
                                <div
                                  key={status.id || idx}
                                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                    pstatus.includes(status.value) ? 'bg-blue-100' : ''
                                  }`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('Status item clicked:', status);
                                    handleSelectDropdownItem('status', status.value, true);
                                  }}>
                                  <div className="flex items-center justify-between gap-3">
                                    <span>{status.label}</span>
                                    {pstatus.includes(status.value) && (
                                      <span className="text-blue-700">âœ“</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </th>
                    <th
                      className="px-4 py-2 border-b border-gray-200 font-medium relative"
                      ref={closureDateHeaderRef}>
                      <span>Expected Closure Date</span>
                      <button
                        className="ml-1 align-middle"
                        data-dropdown-trigger
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('closureDate', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'closureDate' && (
                        <div
                          className="absolute bg-white border rounded shadow z-[1000] top-full left-0 min-w-full"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            minWidth: '100%',
                            maxHeight: '250px',
                            overflowY: 'auto'
                          }}
                          onClick={(e) => {
                            console.log('Closure date dropdown clicked');
                            e.stopPropagation();
                          }}>
                          {[
                            {
                              id: '',
                              value: '',
                              label: closureDateFilter ? 'Clear Filter' : 'Select Date Range',
                              selected: true
                            },
                            ...closureDateOptions
                          ].map((option, idx) => (
                            <div
                              key={option.id || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                option.id === '' && 'bg-gray-100'
                              } ${option.value === closureDateFilter && 'bg-blue-100'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Closure date item clicked:', option);
                                handleSelectDropdownItem('closureDate', option.value);
                              }}>
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </th>
                    <th className="px-4 py-2 border-b border-gray-200 font-medium text-center">
                      {transferBulkMode ? (
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer accent-[#2A565E]"
                            checked={transferSelectAllChecked}
                            onChange={(e) => handleTransferSelectAll(e.target.checked)}
                            title="Select all leads on this page"
                          />
                          <span>All</span>
                        </div>
                      ) : deleteBulkMode ? (
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer accent-rose-600"
                            checked={deleteSelectAllChecked}
                            onChange={(e) => handleDeleteSelectAll(e.target.checked)}
                            title="Select all leads on this page"
                          />
                          <span>All</span>
                        </div>
                      ) : waBulkMode ? (
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer accent-[#2A565E]"
                            checked={waSelectAllChecked}
                            onChange={(e) => handleWaSelectAll(e.target.checked)}
                            title="Select all leads on this page"
                          />
                          <span>All</span>
                        </div>
                      ) : (
                        'Action'
                      )}
                    </th>
                    {/* Edit, Duplicate, Delete actions live in Sales Details */}
                  </tr>
                </thead>
                <tbody>
                  {clientFilterLoading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-10 align-middle">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                          <p className="text-sm text-gray-600">Applying AI filter…</p>
                        </div>
                      </td>
                    </tr>
                  ) : leadsData && leadsData?.length > 0 ? (
                    leadsData?.map((task, index) => {
                      return (
                        <React.Fragment key={`${task?.id}-${index}`}>
                          <tr
                            className="border-b border-gray-100 hover:bg-gray-50 bg-white h-[3.35rem]"
                            onClick={() => {
                              // Ensure returnTo includes the current status filter (e.g. Warm)
                              // even if URL sync is briefly behind local state.
                              const params = buildSalesListFilterParams(
                                new URLSearchParams(location.search),
                                { ...activeFilters, status: pstatus }
                              );
                              const qs = params.toString();
                              const returnTo = `${location.pathname}${qs ? `?${qs}` : ''}`;
                              navigateWithReturn(navigate, `/builder/sales/${task?.id}`, {
                                returnTo
                              });
                            }}>
                            <td className="px-4 py-2 max-w-[15rem] font-semibold text-gray-900">
                              <div className="flex min-w-0 flex-col gap-1">
                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="truncate" title={task?.name || undefined}>
                                    {task?.name || '---'}
                                  </span>
                                  <LeadAiScoreBadge
                                    leadId={task?.id}
                                    scoreByLeadId={scoreByLeadId}
                                    aiScore={task?.ai_score}
                                    aiCategory={task?.ai_category}
                                  />
                                </div>
                                {Array.isArray(task?.qualification_tags) &&
                                  task.qualification_tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {task.qualification_tags.slice(0, 4).map((tag) => (
                                        <span
                                          key={tag}
                                          className="inline-block max-w-[7rem] truncate px-1.5 py-0.5 rounded bg-amber-50 text-amber-900 text-[10px] font-medium"
                                          title={tagLabelByValue[tag] || tag}>
                                          {tagLabelByValue[tag] || tag}
                                        </span>
                                      ))}
                                      {task.qualification_tags.length > 4 && (
                                        <span className="text-[10px] text-gray-500">
                                          +{task.qualification_tags.length - 4}
                                        </span>
                                      )}
                                    </div>
                                  )}
                              </div>
                            </td>
                            {/* <td className="px-4 py-2">{task?.project || '---'}</td> */}
                            <td className="px-4 py-2">{task?.next_follow_up_date || '---'}</td>
                            <td
                              className="px-4 py-2 max-w-[14rem] truncate"
                              title={
                                task?.next_follow_up_agenda && task.next_follow_up_agenda !== '---'
                                  ? task.next_follow_up_agenda
                                  : undefined
                              }>
                              {task?.next_follow_up_agenda || '---'}
                            </td>
                            <td className="px-4 py-2">{task?.created_on || '---'}</td>
                            <td className="px-4 py-2">
                              <div className="flex items-center justify-center w-full relative ">
                                {task?.status === 'Closed Lost' ? (
                                  <span className="text-gray-400">---</span>
                                ) : task?.assigned_to ? (
                                  <div
                                    className={`flex gap-1 justify-start items-center w-full ${canAssignLead ? 'cursor-pointer' : ''}`}
                                    onClick={
                                      canAssignLead
                                        ? (e) => {
                                            e?.stopPropagation();
                                            handleTransfer(task?.id);
                                          }
                                        : undefined
                                    }>
                                    <div
                                      className={`w-6 h-6 rounded-full bg-[#FFB346] text-white flex items-center justify-center text-[0.8rem] font-semibold border-2 border-white leading-none`}>
                                      {task?.assigned_to?.charAt(0)?.toUpperCase() || 'U'}
                                    </div>
                                    {task?.assigned_to}
                                  </div>
                                ) : canAssignLead ? (
                                  <img
                                    src={DP}
                                    alt=""
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      handleTransfer(task?.id);
                                    }}
                                  />
                                ) : (
                                  <span className="text-gray-400">---</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2">{task?.status || '---'}</td>
                            <td className="px-4 py-2 min-w-[10rem]">
                              <div className="flex items-center gap-2 w-full">
                                {task?.expected_clouser_date || '---'}{' '}
                                {Array.isArray(task.dates) && task.dates.length > 1 && (
                                  <>
                                    {`, `}
                                    <img
                                      className="cursor-pointer"
                                      src={CalenderIcon}
                                      alt="calenderIcon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDatesPopover({
                                          dates: task.dates,
                                          anchorRect: e.currentTarget.getBoundingClientRect()
                                        });
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-center">
                              {transferBulkMode ? (
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 cursor-pointer accent-[#2A565E]"
                                  checked={transferSelectedIds.includes(task?.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleTransferLead(task?.id, e.target.checked);
                                  }}
                                  title="Select lead for bulk transfer"
                                />
                              ) : deleteBulkMode ? (
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 cursor-pointer accent-rose-600"
                                  checked={deleteSelectedIds.includes(task?.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleDeleteLead(task?.id, e.target.checked);
                                  }}
                                  title="Select lead for bulk delete"
                                />
                              ) : waBulkMode ? (
                                task?.phone_number && task.phone_number !== '---' ? (
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 cursor-pointer accent-[#2A565E]"
                                    checked={waSelectedLeadIds.includes(task?.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleWaLead(task?.id, e.target.checked);
                                    }}
                                    title="Select lead for WhatsApp flow"
                                  />
                                ) : (
                                  <span className="text-gray-300" title="No phone number">
                                    —
                                  </span>
                                )
                              ) : (
                                <div className="inline-flex items-center justify-center gap-1">
                                  {canDeleteLeads && (
                                    <button
                                      type="button"
                                      title="Delete lead"
                                      aria-label="Delete lead"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCheckDelete(task?.id);
                                      }}
                                      className="inline-flex items-center justify-center rounded-full p-1.5 text-rose-600 transition-colors hover:bg-rose-50">
                                      <Trash2 className="h-4 w-4" strokeWidth={2} />
                                    </button>
                                  )}
                                  {task?.phone_number && task.phone_number !== '---' ? (
                                    <button
                                      type="button"
                                      title="Open WhatsApp conversation"
                                      aria-label="Open WhatsApp conversation"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const normalized = normalizeLeadPhone(task.phone_number);
                                        setAllLeads((prev) =>
                                          prev.map((l) =>
                                            l?.id === task?.id
                                              ? { ...l, unread_whatsapp_count: 0 }
                                              : l
                                          )
                                        );
                                        const rawLead = allLeads?.find((l) => l?.id === task?.id);
                                        const leadProjectId =
                                          rawLead?.projects?.[0]?.id || projectID || '';
                                        navigate(`/builder/sales/whatsapp/${normalized}`, {
                                          state: {
                                            contactName: task?.name,
                                            leadId: task?.id,
                                            projectId: leadProjectId
                                          }
                                        });
                                      }}
                                      className="relative inline-flex items-center justify-center rounded-full p-1.5 text-green-600 transition-colors hover:bg-green-50">
                                      <MessageCircle className="h-5 w-5" strokeWidth={2} />
                                      {Number(task?.unread_whatsapp_count) > 0 && (
                                        <span
                                          className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                                          aria-label={`${task.unread_whatsapp_count} unread WhatsApp messages`}
                                        />
                                      )}
                                    </button>
                                  ) : !canDeleteLeads ? (
                                    <span className="text-gray-300">—</span>
                                  ) : null}
                                </div>
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-10 align-middle">
                        <div className="flex flex-col items-center justify-center w-full h-full">
                          <img
                            src={NoDataFoundImg}
                            alt="No Data"
                            className="w-32 h-28 mx-auto mb-2 object-contain"
                          />
                          <h2 className="text-lg font-bold text-gray-800 mb-1">
                            {isClientAiFilter(aiFilter) ? 'No matching leads' : 'No Data Found'}
                          </h2>
                          <p className="text-gray-600 text-sm">
                            {isClientAiFilter(aiFilter)
                              ? 'No leads match this AI filter in the current project scope.'
                              : 'The page you are trying to access has no data. Proceed with adding data.'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </InfiniteScroll>
          </div>
        </div>
      </GridWrapper>
      </div>
      </>
      )}

      {transferBulkMode && (
        <div className="fixed bottom-6 right-6 z-[60] lg:right-10">
          <div className="flex items-center gap-4 rounded-full border border-gray-200 bg-white px-5 py-3 shadow-lg">
            <span className="text-sm font-medium text-gray-700">
              {transferSelectedIds.length} / {TRANSFER_MAX_LEADS} selected
            </span>
            <button
              type="button"
              onClick={handleOpenBulkTransferModal}
              disabled={transferSelectedIds.length === 0}
              className="flex items-center gap-2 rounded-full bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2A565E]/90 disabled:cursor-not-allowed disabled:opacity-50">
              <Users size={16} />
              Transfer
            </button>
            <button
              type="button"
              onClick={exitTransferBulkMode}
              className="text-sm font-medium text-gray-500 hover:text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      {deleteBulkMode && (
        <div className="fixed bottom-6 right-6 z-[60] lg:right-10">
          <div className="flex items-center gap-4 rounded-full border border-gray-200 bg-white px-5 py-3 shadow-lg">
            <span className="text-sm font-medium text-gray-700">
              {deleteSelectedIds.length} / {DELETE_MAX_LEADS} selected
            </span>
            <button
              type="button"
              onClick={handleConfirmBulkDelete}
              disabled={deleteSelectedIds.length === 0 || isSubmitting}
              className="flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50">
              <Trash2 size={16} />
              Delete
            </button>
            <button
              type="button"
              onClick={exitDeleteBulkMode}
              className="text-sm font-medium text-gray-500 hover:text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      {isBulkTransferModalOpen && (
        <Modal
          width={48}
          name="Bulk transfer leads"
          onClose={() => setIsBulkTransferModalOpen(false)}
          confirmOnClose
          isDirty>
          <BulkTransferSalesUser
            leadIds={transferSelectedIds}
            projectId={transferResolvedProjectId}
            onClose={() => setIsBulkTransferModalOpen(false)}
            onSuccess={async () => {
              await refreshLeadsAfterTransfer();
              exitTransferBulkMode();
            }}
          />
        </Modal>
      )}

      {waBulkMode && (
        <div className="fixed bottom-6 right-6 z-[60] lg:right-10">
          <div className="flex items-center gap-4 rounded-full border border-gray-200 bg-white px-5 py-3 shadow-lg">
            <span className="text-sm font-medium text-gray-700">
              {waSelectedLeadIds.length} / {WA_MAX_LEADS} selected
            </span>
            <button
              type="button"
              onClick={handleOpenWaFlowPicker}
              disabled={waSelectedLeadIds.length === 0}
              className="flex items-center gap-2 rounded-full bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2A565E]/90 disabled:cursor-not-allowed disabled:opacity-50">
              <MessageCircle size={16} />
              Send WhatsApp
            </button>
            <button
              type="button"
              onClick={exitWaBulkMode}
              className="text-sm font-medium text-gray-500 hover:text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      {waFlowPickerOpen && (
        <BulkSendFlowModal
          projectId={waResolvedProjectId}
          leadIds={waSelectedLeadIds}
          onClose={() => setWaFlowPickerOpen(false)}
          onSent={exitWaBulkMode}
        />
      )}

      <QualificationQuestionsBankModal
        isOpen={showQualQuestionsModal}
        onClose={() => setShowQualQuestionsModal(false)}
      />

      {datesPopover &&
        createPortal(
          <div
            data-dropdown-menu
            className="bg-white border rounded shadow-lg z-[2000] p-3 min-w-[12rem]"
            style={{
              position: 'fixed',
              left: datesPopover.anchorRect.left,
              top: datesPopover.anchorRect.bottom + 4
            }}
            onClick={(e) => e.stopPropagation()}>
            <p className="text-xs font-semibold text-gray-700 mb-2">Closure dates</p>
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {(datesPopover.dates || []).map((d, i) => {
                const raw = d?.date || d?.expected_clouser_date || d?.created_at || d;
                const label =
                  typeof raw === 'string' || raw instanceof Date
                    ? formatDate(raw)
                    : '---';
                return (
                  <li key={d?.id || i} className="text-sm text-gray-800">
                    {label}
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SalesList;
